<template>
  <pos-layout>
    <template #navbar>
      <OrderSearchBar
        ref="orderSearchBar"
        :search="search"
        :currency="currency"
        :transform-color="transformColor"
        :get-display-name="getSearchProductDisplayName"
        @input="onInputChangeSearch"
        @enter="findProduct"
        @select-product="addItemToOrder"
        @barcode-confirm="searchByBarcode"
        @barcode-input-change="barcodeInputChange"
        @hide-barcode-numpad="hideBarcodeNumpad"
        @hide="hideSearch"
      />
    </template>

    <template #clock-right>
      <router-link
        :to="{ name: 'OrdersList' }"
        class="header-action-btn text-white font-weight-bold text-decoration-none"
        title="All Orders"
      >
        <svg class="icon icon-icon-order-invoice">
          <use xlink:href="#icon-icon-order-invoice" />
        </svg>
        <span class="header-action-btn__text">All Orders</span>
      </router-link>
    </template>
    
    <div class="h-100 orders d-print-none">
      <!-- Print panel For mobile -->
      <div class="print d-md-none d-print-none">
        <b-row class="h-100 no-gutters">
          <b-col
            v-if="shouldShowTablesButton"
            cols="3"
            class="h-100 d-md-none"
          >
            <b-button
              :to="isSavingProcess?{}:{ name: 'Tables', params: { id: currentOrder.table }}"
              variant="link"
              class="w-100 bg-pink"
            >
              <svg class="icon icon-icon-table">
                <use xlink:href="#icon-icon-table" />
              </svg>

              <span>
                {{ $t("amountandpaymentmethod.print-to-tables-mob") }}
              </span>
            </b-button>
          </b-col>

          <b-col
            v-if="!shouldShowTablesButton"
            cols="3"
            class="h-100 d-md-none"
          >
            <b-button
              variant="link"
              class="w-100"
              @click="$bvModal.show('more-options-modal')"
            >
              <svg class="icon icon-icon-more-options">
                <use xlink:href="#icon-icon-more-options" />
              </svg>

              <span>
                {{ $t("amountandpaymentmethod.more-options") }}
              </span>
            </b-button>
          </b-col>

          <b-col
            cols="3"
            class="h-100"
          >
            <b-button
              :class="{'btn-disabled': itemsAtCurrentOrder.length === 0}"
              variant="link"
              class="w-100"
              @click="printCheck()"
            >
              <svg class="icon icon-icon-print-1">
                <use xlink:href="#icon-icon-print-1" />
              </svg>
              <span>
                {{ $t("amountandpaymentmethod.print-check") }}
              </span>
            </b-button>
          </b-col>
          <b-col
            v-if="!isRetailMode"
            cols="3"
            class="h-100"
          >
            <div
              v-if="isKitchenPrintProcessing"
              class="text-center spiner-overbutton"
            >
              <b-spinner
                variant="primary"
                label="Text Centered"
              />
            </div>
            <b-button
              :class="{'btn-disabled': !kitchenUpdatesAvailable || isKitchenPrintProcessing}"
              variant="link"
              class="w-100"
              @click="printKitchenCheck()"
            >
              <svg class="icon icon-icon-print-2">
                <use xlink:href="#icon-icon-print-2" />
              </svg>

              <span>
                {{ $t("amountandpaymentmethod.kitchen-print") }}
              </span>
            </b-button>
          </b-col>
          <b-col
            v-if="isRetailMode"
            cols="3"
            class="h-100"
          >
            <b-button
              variant="link"
              class="w-100"
              @click="openOrderDiscount()"
            >
              <svg class="icon icon-icon-discount">
                <use xlink:href="#icon-icon-discount" />
              </svg>

              <span>
                {{ $t("discountpopup.discount") }}
              </span>
            </b-button>
          </b-col>
          <b-col
            cols="3"
            class="h-100 d-none d-md-block"
          >
            <b-button
              variant="link"
              class="w-100"
            >
              <svg class="icon icon-icon-print-3">
                <use xlink:href="#icon-icon-print-3" />
              </svg>

              <span>
                {{ $t("amountandpaymentmethod.open-drawer") }}
              </span>
            </b-button>
          </b-col>
          <b-col
            cols="3"
            class="h-100 d-none d-md-block"
          >
            <b-button
              variant="link"
              class="w-100"
            >
              <svg class="icon icon-icon-more-options">
                <use xlink:href="#icon-icon-more-options" />
              </svg>

              <span>
                {{ $t("amountandpaymentmethod.more-options") }}
              </span>
            </b-button>
          </b-col>

          <b-col
            v-if="!sidebarMob"
            cols="3"
            class="h-100 d-md-none"
          >
            <b-button
              variant="link"
              class="w-100 bg-primary"
              @click="sidebarMob = !sidebarMob"
            >
              <svg class="icon icon-icon-mob-add">
                <use xlink:href="#icon-icon-mob-add" />
              </svg>

              <span>
                {{ $t("amountandpaymentmethod.print-add-items-mob") }}
              </span>
            </b-button>
          </b-col>

          <b-col
            v-if="sidebarMob"
            cols="3"
            class="h-100 d-md-none"
          >
            <b-button
              variant="link"
              class="w-100 bg-info"
              @click="sidebarMob = !sidebarMob"
            >
              <svg class="icon icon-icon-mob-view">
                <use xlink:href="#icon-icon-mob-view" />
              </svg>

              <span>
                {{ $t("amountandpaymentmethod.print-view-order-mob") }}
              </span>

              <span class="badge">
                <transition name="bounce">
                  <i
                    :key="itemsAtCurrentOrderLength"
                  >{{ itemsAtCurrentOrderLength }}
                  </i>
                </transition>
              </span>
            </b-button>
          </b-col>
        </b-row>
      </div>

      <b-row class="h-100 no-gutters d-print-none">
        <b-col
          :class="{ hide: sidebarMob }"
          col
          cols="12"
          md="5"
          lg="4"
          class="h-100 orders-sidebar-mob"
        >
          <div
            :class="{ 'orders-sidebar-kiosk': isKioskMode }"
            class="orders-sidebar"
          >
            <div
              :aria-pressed="modifiersShow ? 'OrderCheckout' : 'false'"
              class="modifiers-sidebar-backdrop"
              @click="modifiersSave"
            />

            <OrderPanel
              :current-order="currentOrder"
              :discount-employee="discountEmployee"
              :order-panel-order-dining-option="orderDiningOption"
              :default-dining-option="defaultDiningOption"
              :payments-cash-entries="paymentsCashEntries"
              :current-order-sum="currentOrderSum"
              :order-panel-table="table"
              :is-display-payment-details="isDisplayPaymentDetails"
              :is-load="isLoad"
              :all-discounts="allDiscounts"
              :discount="discount"
              :order-items="orderItems"
              @showSelectCustomerModal="showSelectCustomerModal"
              @showDiningOptionsModal="showDiningOptionsModal"
              @showPriceAdjustModal="showPriceAdjustModal"
              @showNotesModal="showNotesModal"
              @displayPaymentDetails="displayPaymentDetails"
              @goToCheckout="goToCheckout"
              @holdOrder="holdOrder"
              @saveDiscountToOrder="saveDiscountToOrder"
              @changeDiscount="changeDiscount"
              @modifiersOpen="modifiersOpen"
              @showPointsRedemptionModal="openPointsRedemptionModal"
              @cancelOrder="cancelOrder"
              :effective-customer-points="currentOrder.customer ? ((currentOrder.customer.rewards_points || currentOrder.customer.reward_points || currentOrder.customer.points_balance || 0) + (currentOrder.items.find(i => i.name === 'Points Redeemed' && !i.void_status) ? currentOrder.items.find(i => i.name === 'Points Redeemed' && !i.void_status).quantity : 0)) : 0"
            />
          </div>
        </b-col>

        <b-col
          v-if="showQtyBar && !modifiersShow"
          class="h-100 d-none d-md-block p-0"
          style="flex: 0 0 80px; max-width: 80px;"
        >
          <QuantityBar
            ref="quantityBarDesktop"
            @quantity-selected="onQuantitySelected"
          />
        </b-col>

        <b-col
          col
          cols="12"
          :md="showQtyBar ? 'auto' : '7'"
          :lg="showQtyBar ? 'auto' : '8'"
          class="h-100"
          :style="showQtyBar ? 'flex: 1 1 auto;' : ''"
        >
          <div class="products-wrap h-100">
            <CategorySwiper
              :categories="displayCategories"
              :swiper-option="swiperOption"
              :transform-color="transformColor"
              :current-category="currentCategory"
              @select="selectCategory"
            />

            <ProductGrid
              :paginated-products="paginatedProducts"
              :transform-color="transformColor"
              :currency="currency"
              :is-kiosk-mode="isKioskMode"
              :is-product86d="isProduct86d"
              :get-product-display-name="getProductDisplayName"
              @product-press-start="onProductPressStart"
              @product-press-end="onProductPressEnd"
              @product-tile-click="onProductTileClick"
              @show-product-info="showProductInfo"
            />

            <!-- Single shared product-info modal (Phase 4): one instance driven
                 by selectedInfoProduct, replacing the previous one-modal-per-tile
                 pattern rendered inside the product v-for. -->
            <b-modal
              id="modal-show-info"
              hide-footer
              modal-class="info-popup"
            >
              <template v-if="selectedInfoProduct">
                <div
                  :style="{ background: transformColor[selectedInfoProduct.color.toLowerCase()] ? transformColor[selectedInfoProduct.color.toLowerCase()] : selectedInfoProduct.color}"
                  class="products-info-thumb"
                >
                  <lazy-image
                    v-if="selectedInfoProduct.image"
                    :src="selectedInfoProduct.image"
                    :alt="selectedInfoProduct.name"
                    class="img-responsive"
                  />
                </div>
                <div class="products-info-body">
                  <h5>
                    {{ getProductDisplayName(selectedInfoProduct) }}
                  </h5>

                  <div class="text">
                    {{ selectedInfoProduct.description }}
                  </div>
                </div>
              </template>
            </b-modal>

            <ModifierPanel
              :show="modifiersShow"
              :modifiers="modifiers"
              :modifier-group-vertical="modifierGroupVertical"
              :currency="currency"
              :get-product-display-name="getProductDisplayName"
              :count-selected-modifiers="countSelectedModifiers"
              :modifier-group-rules="modifierGroupRules"
              :is-modifier-selected="isModifierSelected"
              :is-modifier86d="isModifier86d"
              :get-text-color="getTextColor"
              @delete-item="deleteItemFromModifiers"
              @save="modifiersSave"
              @toggle="toggleModifierForItem"
            />
          </div>
        </b-col>
      </b-row>
    </div>
    <div class="d-none d-print-block">
      <PdfReceiptView :order="currentOrder" />
    </div>
    <ModalQuantity
      @save="saveNewQuantity"
    />
    <ModalPointsRedemption
      :customer-points="currentOrder.customer ? (currentOrder.customer.rewards_points || currentOrder.customer.reward_points || currentOrder.customer.points_balance || 0) : 0"
      :max-redeemable-points="maxRedeemablePoints"
      :conversion-rate="rewardsConversionRate"
      :initial-points="currentPointsRedemption"
      @save="savePointsRedemption"
    />
    <OrderDiscountModal
      :discount="discount"
      :currency="currency"
      :all-discounts="allDiscounts"
      :body-bg-variant="bodyBgVariant"
      :popup-price="popupPrice"
      :popup-price-sidebar="popupPriceSidebar"
      @change-type="changeDiscountType"
      @type-digit="typeDiscount"
      @save="saveDiscountToItem"
      @set-predefined="setPredefinedDiscount"
    />
    <EnterPinModal @verify-action="verifyAction" />

    <SimpleInfoModal :id="'modal-product-not-found'">
      <template v-slot:title>
        Not Found
      </template>
      <template v-slot:info>
        No product or order found with that search. {{ $t("modal-info.try-again") }}
      </template>
    </SimpleInfoModal>

    <SelectCustomer
      :order-customer="currentOrder.customer"
      :order-discount-employee="currentOrder.discount_employee"
      :employees="employees"
      @select-customer="selectCustomer"
      @select-customer-after-add="selectCustomer"
      @remove-customer="removeCustomerFromOrder"
      @select-employee="selectEmployeeForDiscount"
      @remove-employee="removeEmployeeFromOrder"
    />
    <DiningOptionModal
      v-if="terminal"
      :dining-options="terminal.dining_options"
      :dining-option="currentOrder.dining_option_type"
      @select-dining-option="selectDiningOption"
    />
    <CustomerModal
      :view-customer="viewCustomer"
      :is-selected="true"
      :index="1"
      @close-modal="clearViewCustomer"
    />
    <CustomerModalSimple
      @select-customer-after-add="selectCustomer"
    />
    <ModalItemNotes @saveNote="saveNote($event)" />
    <ModalPriceAdjust @savePrice="savePrice($event)" />
    <ModalPriceAdjust
      id="gift-card-modal"
      @saveGiftCard="saveGiftCard($event)"
    />
    <scale-loader
      :loading="isLoad"
      :color="'#007bff'"
    />
    <ModalError
      :id="errorModal.modalId"
      :error="errorModal.errorBody"
      @hide-modal="errorModalCallBack"
    />

    <b-modal
      id="modal-product-86"
      title="Product availability"
      hide-footer
      centered
      @hidden="onProduct86ModalHidden"
    >
      <p
        v-if="product86MenuProduct"
        class="mb-3"
      >
        <strong>{{ getProductDisplayName(product86MenuProduct) }}</strong>
      </p>
      <p class="text-muted small mb-3">
        When enabled, the item is disabled on POS and removed from kiosk and online ordering until turned off.
      </p>
      <div
        v-if="product86MenuProduct"
        class="d-flex align-items-center justify-content-between border rounded px-3 py-3 bg-light"
      >
        <div class="pr-3">
          <div class="font-weight-bold">
            Out of stock (86)
          </div>
          <div class="text-muted small mb-0">
            {{ product86ToggleValue ? 'On — guests cannot order this item.' : 'Off — item is available.' }}
          </div>
        </div>
        <b-form-checkbox
          v-model="product86ToggleValue"
          switch
          size="lg"
          class="switch-product-86 m-0"
          @change="onProduct86ToggleChange"
        />
      </div>
      <template
        v-if="product86MenuProduct && product86MenuProduct.modifier_groups && product86MenuProduct.modifier_groups.length"
      >
        <hr class="my-3">
        <p class="font-weight-bold small mb-1">
          Modifiers
        </p>
        <p class="text-muted small mb-2">
          Turn off options that are out of stock for this item. Changes sync to POS, kiosk, and online ordering.
        </p>
        <div class="product-86-modifiers-scroll">
          <div
            v-for="grp in product86MenuProduct.modifier_groups"
            :key="'86-grp-' + grp.id"
            class="mb-2"
          >
            <div class="font-weight-bold small text-secondary mb-1">
              {{ grp.name }}
            </div>
            <div
              v-for="mod in (grp.modifiers || []).filter((m) => m && m.active)"
              :key="'86-mod-' + grp.id + '-' + mod.id"
              class="d-flex align-items-center justify-content-between border rounded px-3 py-2 mb-2 bg-white"
            >
              <span class="pr-2 small">{{ mod.name }}</span>
              <b-form-checkbox
                :checked="isModifier86d(product86MenuProduct.id, mod.id)"
                switch
                class="m-0 switch-modifier-86"
                @input="onModifier86Toggle(mod.id, $event)"
              />
            </div>
          </div>
        </div>
      </template>
    </b-modal>
  </pos-layout>
</template>

<script>
import router from "@/router";
import { ORDER_STATUS } from "@/helpers/order/status";
import { EMPLOYEE_ROLE } from "@/helpers/employee/role";
import orderMixin from "@/mixins/order/orderMixin";
import printMixin from "@/mixins/printMixin";
import syncMixin from "@/mixins/syncMixin";
import orderModifierMixin from "@/mixins/order/modifier";
import { COLORS } from "@/helpers/colors";
import viewCustomerModalMixin from "@/mixins/modals/viewCustomerModalMixin";
import PosLayout from "@/layouts/PosLayout";
import ModalItemNotes from "@/components/ModalItemNotes";
import ModalPriceAdjust from "@/modals/ModalPriceAdjust";
import SimpleInfoModal from "@/components/SimpleInfoModal";
import SelectCustomer from "@/modals/SelectCustomerModal";
import CustomerModal from "@/modals/CustomerModal";
import CustomerModalSimple from "@/modals/CustomerModalSimple";
import PdfReceiptView from "@/components/PdfReceiptView";
import EnterPinModal from "@/modals/EnterPinModal";
import DiningOptionModal from "@/modals/Order/DiningOptionModal";
import { TAX_OPTION } from "@/helpers/store/tax_options";
import ScaleLoader from "vue-spinner/src/ScaleLoader.vue";
import { toFixed } from "@/helpers/utils";
import {
  calculateItemTaxTotal,
  getItemGrandTotal,
  getItemSubtotal,
  getOrderGrantTotal,
  setTaxRound,
} from "@/helpers/calc";
import ModalQuantity from "@/modals/ModalQuantity";
import ModalPointsRedemption from "@/modals/ModalPointsRedemption";
import { computePointsContext } from "@/pricing";
import { mapActions, mapGetters } from "vuex";
import OrderPanel from "@/components/Order/OrderPanel"
import QuantityBar from "@/components/Order/QuantityBar"
import cdOrderMixin from "@/mixins/cdOrderMixin";
import ModalError from "@/modals/ModalError";
// import { StreamBarcodeReader } from "vue-barcode-reader";
import { GIFT_CARD_TRANSACTION_TYPE } from "@/helpers/order/transaction/types";
import { applyPromotionsToCart } from "@/helpers/promotionHelper";
import { promotionItemFlags } from "@/helpers/order/promotionSyncNormalize";
import { normalizeOrderDiscounts } from "@/helpers/order/normalizeOrderDiscounts";
import liveObjectsService from "@/plugins/liveObjects";
import DashboardService from "@/services/dashboard-service";
import indexDbService from "@/helpers/dbHelper";
import {ORDER_QUEUE_DEBOUNCE_INTERVAL_IN_MS} from "@/store/appConstants";
import LazyImage from "@/components/Common/LazyImage.vue";
import CategorySwiper from "@/components/Order/CategorySwiper.vue";
import OrderDiscountModal from "@/components/Order/OrderDiscountModal.vue";
import OrderSearchBar from "@/components/Order/OrderSearchBar.vue";
import ModifierPanel from "@/components/Order/ModifierPanel.vue";
import ProductGrid from "@/components/Order/ProductGrid.vue";
import { isTerminalSettingEnabled } from "@/helpers/terminal/settings";
import { isMembershipScanValue, resolveMemberScanCustomer } from "@/helpers/customer/memberScan";
import { checkForSKU } from "@/helpers/order/barcode";
import { getReadableTextColor } from "@/helpers/ui/textColor";
import { getModifierGroupRules } from "@/helpers/order/modifiers";
import { PAYMENT_BEHAVIOR } from "@/helpers/order/payment/behavior";
import { sendDejavooCart } from "@/helpers/payments/DejavooCart";
import {
  createBaseOrderItem,
  getItemModifierSignature,
  getGroupableItemPrice,
  canGroupOrderItems,
  findGroupableOrderItemIndex,
  applyDiningOption,
  resolveProductDisplayName,
  resolveSearchProductDisplayName,
} from "@/services/order/orderDomain";

export default {
  name: "Order",
  components: {
    CustomerModal,
    CustomerModalSimple,
    EnterPinModal,
    PosLayout,
    ModalItemNotes,
    ModalPriceAdjust,
    SimpleInfoModal,
    SelectCustomer,
    DiningOptionModal,
    PdfReceiptView,
    ScaleLoader,
    ModalQuantity,
    ModalPointsRedemption,
    OrderPanel,
    QuantityBar,
    ModalError,
    LazyImage,
    CategorySwiper,
    OrderDiscountModal,
    OrderSearchBar,
    ModifierPanel,
    ProductGrid
  },
  mixins: [
    orderMixin,
    printMixin,
    orderModifierMixin,
    viewCustomerModalMixin,
    syncMixin,
    cdOrderMixin,
  ],
  data() {
    return {
      unsubscribeOrdersListener: null,
      dejavooCartSendTimer: null,
      // Product selected for the single shared product-info modal (Phase 4).
      selectedInfoProduct: null,
      bufferHelper: {
        itemsSavingCounter: 0,
        nextRoute: "",
      },
      visible: false,
      isLoad: false,
      syncing: null,
      initialSyncStarted: false,
      layout: "normal",
      sidebarMob: true,
      baseURL: null,
      isTaxed: true,
      swiperOption: {
        slidesPerView: "auto",
        spaceBetween: 0,
        freeMode: true,
        slideToClickedSlide: true,
        threshold: 30,
        pagination: {
          el: ".swiper-pagination",
          clickable: true,
        },
        breakpoints: {
          992: {
            slidesPerView: "auto",
            spaceBetween: 0,
            freeMode: true,
            slideToClickedSlide: false,
          },
        },
      },
      search: {
        value: "",
        visible: false,
        results: [],
        canScrollUp: false,
        canScrollDown: false,
      },
      currentCategory: 0,
      itemIdCounter: 0,
      // Phase 3 keystone: `currentOrder` is now a computed get/set proxy over the
      // Vuex `orders` state (single source of truth), keyed by `activeOrderKey`.
      // `draftActiveOrder` is the fallback the proxy returns BEFORE the order exists
      // in the store (route id === "new", or a brand-new order not yet persisted).
      // Once the order lands in the store, the proxy returns the live store
      // object and component mutations land directly in Vuex (strict mode is off).
      draftActiveOrder: {
        customer: null,
        localID: 0,
        subtotal: 0,
        items: [],
        table: null,
        tax_total: 0,
        discount_total: 0,
        order_discount: 0,
        status: 1,
        open_date: null,
        number: "0",
        discount_orders: 0,
        payments: [],
        receipt_kitchen_printed: false,
        description: "",
        dining_option_type: null,
        cash_discount: false,
        discount_orders_raw_value: null,
        discount_orders_raw_type: null,
        // order_gift_card_payment:[],
        isActive:false //isActive variable to determine if current order is open the pos window
      },
      activeOrderKey: null,
      discount: {
        val: "",
        changed: false,
        type: 1
      },
      orders_discount: {
        val: 0,
        changed: false,
        type: 1
      },
      bodyBgVariant: "light",
      popupPrice: ["popup-price"],
      popupPriceSidebar: ["popup-price-sidebar"],
      currentEmployee: null,
      // allDiscounts: [],
      events: {},
      isDisplayPaymentDetails: false,
      transformColor: COLORS,
      isVerified: true,
      lockedAction: false,
      lockedItemId: 0,
      lockedVoidReason: null,
      paginatedProducts: [],
      pagination: {
        page: 1,
        perPage: 50,
        pageCount: 0
      },
      storeInfo: {},
      filteredProductsByCategory: [],
      default_dining_option: null,
      modalItemIndex: null,
      releaseLockedBy: true,
      firstSync:false,
      errorModal:{
        modalId: "complete-order-error",
        errorBody: {
          title: "Order you are trying to open is completed",
          info: `You can edit only Open orders...`
        }
      },
      isAddingItem: false,
      itemQueue: [],
      isInitializing: false,
      selectedQuantityFromBar: null,
      pendingItemSyncTimeout: null,
      maxRedeemablePoints: 0,
      rewardsConversionRate: 0.01,
      currentPointsRedemption: 0,
      queueSyncTimeout: null,
      queueSyncTimeoutDebounce: ORDER_QUEUE_DEBOUNCE_INTERVAL_IN_MS,
      product86LongPressTimer: null,
      product86LongPressConsumed: false,
      product86MenuProduct: null,
      product86ToggleValue: false,
      activePresenceOrderId: null,
    };
  },
  computed: {
    ...mapGetters({
      isTaxInclusive: "config/isTaxInclusive",
      isOnline: "connection",
      terminalMode: "terminalMode",
      isShiftOpen: "isShiftOpen",
      products: "config/products",
      categories: "config/categories",
      employee: "employee",
      employeeById: "config/employeeById",
      employees: "config/employees",
      store: "config/store",
      isSavingProcess: "isSavingProcess",
      isKioskMode: "config/isKioskMode",
      updatingOrder: "orders/getUpdatingOrder",
      currency: "config/currency",
      refundItems: "orders/refundItems",
      refundOrderId: "orders/refundOrderId",
      refundedOrderTableId: "orders/refundedOrderTableId",
      emptyCurrentOrder: "orders/emptyCurrentOrder",
      refundedOrdersList: "orders/refundedOrdersList",
      allDiscounts: "config/discounts", // it send back all the discount presets
      refundedOrderDiscountTotalDetails:"orders/refundedOrderDiscountTotalDetails", // discount totsl attributed of the reference order
      discountDetails: "discount/discountDetails",
      terminal: 'config/terminal',
      orders: 'orders/getAllOrders',
      discountArray:'config/discounts',
      voidReasons:'config/voidReasons',
      orderInitailSync:'orders/orderInitailSync',
      allTables:"config/getAllTables",
      activeGiftCard:"giftVoucher/activeGiftCard",
      selectedGiftCards:"giftVoucher/selectedGiftCards",
      showSelectedOrder: 'orders/showSelectedOrder',
      allPromotions: 'promotions/allPromotions',
      modifierGroupVertical: 'config/modifierGroupVertical',
      isProduct86d: 'products86d/isProduct86d',
      isModifier86d: 'products86d/isModifier86d'
    }),
    discountEmployee() {
      return this.currentOrder && this.currentOrder.discount_employee
        ? this.employeeById(this.currentOrder.discount_employee)
        : null;
    },
    // ── Active-order proxy (Phase 3 keystone) ───────────────────────────────
    // get: returns the live Vuex order keyed by `activeOrderKey`, else the
    //      local draft (pre-persist). set: binds the proxy to a store-backed
    //      order, or stashes a blank draft when there is no id/local_id yet.
    currentOrder: {
      get() {
        if (this.activeOrderKey != null) {
          const stored = this.$store.getters["orders/getOrderByIdOrLocalId"](this.activeOrderKey);
          if (stored) return stored;
        }
        return this.draftActiveOrder;
      },
      set(value) {
        if (!value) {
          this.draftActiveOrder = value;
          this.activeOrderKey = null;
          return;
        }
        const key = value.id || value.local_id;
        if (key != null) {
          // Store-backed order (load or freshly-created). Ensure it exists in the
          // store, then point the proxy at the live copy. If `value` is a detached
          // copy/clone of an order already in the store, the live store object
          // already carries any in-place mutations, so we simply re-point.
          const existing = this.$store.getters["orders/getOrderByIdOrLocalId"](key);
          if (!existing) {
            this.$store.dispatch("orders/saveOrder", value);
          }
          this.activeOrderKey = key;
        } else {
          // Blank / new draft (id:0 and localID:0 count as no key).
          this.draftActiveOrder = value;
          this.activeOrderKey = null;
        }
      },
    },
    // Stable signature of the order's BUSINESS state (excludes volatile sync
    // fields). Watched in place of the removed deep `currentOrder` watcher so a
    // saveOrder restamp of updated_at/synced_at/updated does NOT re-trigger sync
    // (which would loop now that currentOrder IS the store object).
    orderBusinessSignature() {
      const o = this.currentOrder;
      if (!o) return "";
      // eslint-disable-next-line no-unused-vars
      const { updated_at, synced_at, updated, created_at, ...rest } = o;
      try {
        return JSON.stringify(rest);
      } catch (e) {
        return String(Date.now());
      }
    },
    // The gift-card product id is configured per store (Store.gift_card,
    // default -2) — never hardcode -2. Number() guards against a string id
    // coming back from the API/IndexedDB. Used by every place that must keep
    // the gift card out of the grid and search (it's added only via
    // "More Options" > Gift Card).
    giftCardProductId() {
      const id = this.store && this.store.gift_card != null ? this.store.gift_card : -2;
      return Number(id);
    },
    displayCategories() {
      const hiddenProductIds = new Set([this.giftCardProductId, -3]);
      const products = Array.isArray(this.products) ? this.products : [];
      const categories = Array.isArray(this.categories) ? this.categories : [];

      return categories.filter(category => {
        if (!category || category.id == null) return false;
        return products.some(product => {
          if (!product) return false;
          if (hiddenProductIds.has(Number(product.id)) || product.name === 'Points Redeemed') return false;
          if (!Array.isArray(product.categories) || !product.categories.includes(category.id)) return false;
          if (this.isKioskMode) {
            if (!product.order_kiosk) return false;
            if (this.isProduct86d(product.id)) return false;
            return true;
          }
          return true;
        });
      });
    },
    isRetailMode() {
      // Check if retail_mode db_setting is true
      return isTerminalSettingEnabled(this.terminal, 'retail_mode');
    },
    showQtyBar() {
      // Check if show_qty_bar db_setting is true
      return isTerminalSettingEnabled(this.terminal, 'show_qty_bar');
    },
    isProduct86ControlsEnabled() {
      // Long-press product availability (86) UI — only when is_86 db_setting is true
      return isTerminalSettingEnabled(this.terminal, 'is_86');
    },
    shouldShowTablesButton() {
      // Show tables button only if in TABLE_SERVICE mode OR show_tables_menu is true
      if (this.terminalMode === "TABLE_SERVICE") {
        return true;
      }
      return isTerminalSettingEnabled(this.terminal, 'show_tables_menu');
    },
    isReceiptSearchEnabled() {
      // Check if search_receipt db_setting is true (disabled by default)
      return isTerminalSettingEnabled(this.terminal, 'search_receipt');
    },
    currentOrderSum: function () {
      const orderSumValue =
        getOrderGrantTotal(
          this.currentOrder,
          this.products,
          this.isTaxInclusive,
        ) - this.currentOrderCashSum;
      return parseFloat(orderSumValue.toFixed(2));
    },
    paymentsCashEntries: function () {
      return this.currentOrder.payments ? this.currentOrder.payments : [];
    },
    currentOrderCashSum: function () {
      let sum = 0;
      this.paymentsCashEntries.map(payment => sum += payment.amount);
      return sum;
    },
    defaultDiningOption () {
      if (!this.terminal) return null;
      return this.terminal["dining_options"].find(option => this.store.default_dining_option === option.id);
    },
    orderItems() {
      return this.currentOrder.items.filter(function (item) {
        return item.void_status !== 1;
      });
    },
  },
  watch: {
    currentOrderSum (value) {
      this.currentOrder.balance_remaining = Math.max(0, value);
    },
    modifiersShow (value) {
      if (!value) this.modifiersSave();
    },
    currentCategory: {
      handler () {
        this.applyProductCategoryFilter();
      },
      immediate:true
  },
    "$store.state.products86d.productIds": {
      deep: true,
      handler () {
        if (!this.isKioskMode) return;
        this.applyProductCategoryFilter();
      }
    },
    ["currentOrder.id"](value, oldId) {
      // Don't update route during batch operations
      if (value && !this._batchAddingItems && value !== oldId) {
        router.replace({
          name: "Order", params: {id: value}
        }, () => {});
      }
    },
    updatingOrder(newValue) {
      if (!newValue || !this.currentOrder || this.currentOrder.local_id !== newValue.local_id) return;
      const remoteId = newValue.remote_id;
      if (remoteId != null) {
        this.$set(this.currentOrder, 'id', remoteId);
        if (!this.currentOrder.number || this.currentOrder.number === "0") {
          this.$set(this.currentOrder, 'number', String(remoteId));
        }
      }
    },
    // Replaces the removed deep `currentOrder` watcher. `currentOrder` is now the
    // live Vuex object, so a deep watcher would re-fire on every saveOrder
    // restamp and loop. Watching the business signature (volatile sync fields
    // excluded) fires on real edits only — preserving the old eager queue sync
    // and customer-display broadcast at every mutation point.
    orderBusinessSignature() {
      if (this.isInitializing) return;

      this.sendOrderToChannel(this.currentOrder)
      this.sendCartToDejavooTerminal()

      const status = this.currentOrder && this.currentOrder.status;
      if (status === ORDER_STATUS.COMPLETED ||
          status === ORDER_STATUS.CANCELLED ||
          status === ORDER_STATUS.REFUNDED) return;

      this.scheduleQueueSync();
    },
    emptyCurrentOrder: {
      immediate: true,
      async handler() {
        if (this.emptyCurrentOrder) {
          await this.emptyOrder()
          this.sendOrderToChannel(this.currentOrder)
          this.setEmptyCurrentOrder(false)
        }
       }
    },
    '$route.query.productId': {
      handler(newProductId) {
        // Handle productId query parameter when route changes
        if (newProductId && this.$route.params.id === "new") {
          const productId = parseInt(newProductId);
          const product = this.products.find(p => p.id === productId);
          if (product) {
            this.$nextTick(() => {
              this.addItemToOrder(product);
              // Clear the query parameter after adding the product
              this.$router.replace({
                name: "Order",
                params: { id: "new" }
              });
            });
          }
        }
      }
    },
    "currentOrder.isActive": {
      handler(isActive) {
        try {
          // setting active state in order to control the showing of leaveCurrentModal on the isActive attribute
            if (isActive)
              this.setActiveOrderState(true)
            else {
              this.setActiveOrderState(false)
            }
        }
        catch (err) {
          //pass
        }
      },
      immediate: true,
    },
    '$route.params.id': {
      handler: function(newId, oldId) {
        // Only re-init if the route actually changed to a different order (not on initial load)
        // Don't trigger when switching from 'new' to the new order's local_id
        if (newId && oldId && newId !== oldId && newId !== 'new' && oldId !== 'new') {
          this.init();
        }
      },
      immediate: false
    },
    // The `storeOrder` store→local merge watcher was removed: with the
    // single-source-of-truth proxy, `currentOrder` already IS the live store
    // object, so there is nothing to merge back from the store.
    'currentOrder.items': {
      deep: true,
      handler() {
        if (this.isInitializing || !this.currentOrder || !this.currentOrder.items) return;
        if (this.isAddingItem) return;
        if (this._promotionDebounce) clearTimeout(this._promotionDebounce);
        this._promotionDebounce = setTimeout(() => {
          const activePromotions = this.$store.getters['promotions/activePromotions'];

          if (activePromotions && activePromotions.length > 0) {
            this.reEvaluatePromotions();
          }
        }, 300);
      }
    },
  },
  async created() {
    // Kiosk order screen: default to All Items before any await (async created can run mounted before later lines)
    if (this.isKioskMode || this.$route.name === "OrderV2") {
      this.currentCategory = "all";
    }
    // fetching data from index db when state is loss
    if(this.orders.length === 0)
      await this.fetchOrdersFromDb()
    if (this.categories.length === 0)
      await this.fetchCategoriesFromDb()
    if (this.products.length === 0){
      await this.fetchProductsFromDb()
      // await this.fetchGiftCardsAndPaymentsFromDb()
    }
    if (this.discountArray.length === 0)
      await this.fetchDiscountsFromDb()
    if (this.voidReasons.length === 0)
      await this.fetchVoidReasonsFromDb()
    if(this.allTables.length === 0)
      await this.fetchTablesFromDb()
    if(this.allPromotions.length === 0) {
      await this.fetchPromotionsFromDb()
      if (this.allPromotions.length === 0) {
        await this.fetchPromotions()
      }
    }

    if (!this.$route.params.id) {
      throw Error("Could not get order id from url");
    }
    if (!this.isShiftOpen && !this.isKioskMode) return await this.$router.push({ name: "Shift" });
    if (!this.categories || this.categories.length === 0) {
      throw Error(`No categories available, ${this.categories ? JSON.stringify(this.categories) : this.categories}`);
    }
    // Pre-select category: kiosk ordering uses "all" (All Items); POS uses first category
    if (!this.displayCategories || this.displayCategories.length === 0) {
      throw Error(`No categories available after filtering, ${this.categories ? JSON.stringify(this.categories) : this.categories}`);
    }
    this.currentCategory = (this.isKioskMode || this.$route.name === "OrderV2") ? "all" : this.displayCategories[0].id;
    if (!this.isKioskMode && this.employee.role === EMPLOYEE_ROLE.Cashier) {
      this.isVerified = false;
    }
    this.default_dining_option = this.isKioskMode ? this.$route.params.diningOption : this.store.default_dining_option;

    // if the order is to be refunded check for refunded items.
    if (this.refundItems.length > 0) {

      this.currentOrder.reference_order = this.refundOrderId

      const refundItems = this.refundItems

      const itemsToRefund = refundItems.map(refundItem => {
        // only target those item who status is 0 i.e. they were not deleted previously
        const targetObject = this.products.find(item => item.id === refundItem.product && (refundItem.void_status === 0 || refundItem.void_status === null ) && refundItem.duplicateQuantity >0)

        // setting boolean state so that number of calculations to be done per item can be controlled
        this.setDeductRefundQuantity(true)

        if (targetObject) {
          return {
            quantity: -1 * (refundItem.duplicateQuantity || 1),
            modifiers: refundItem.modifiers,
            refundedPrice : refundItem.price,
            refundedDiscount: refundItem.discount,
            refundItemId: refundItem.id,
            refundModifiers: true,
            refundItemTax_status : refundItem.tax_status,
            ...targetObject
          }
        } else {
          return null
        }
      }).filter(x => !!x)

      // Set a flag to prevent syncing during batch item addition
      this._batchAddingItems = true;

      for (let index = 0; index < itemsToRefund.length; index++) {
        const item = itemsToRefund[index]
        // add all the refunded items to the order
        await this.addItemToOrder(item)
      }

      // Clear the flag and sync once after all items are added
      this._batchAddingItems = false;
      await this.sync(false, false);

      this.setRefundItems([])
    }

    await this.init();
    if (this.$route.params.id && this.$route.params.id !== "new") {
      this.checkForCompletedOrder(this.$route.params.id)
    }
  },
  async beforeDestroy () {
    await this.releaseOrderPresence();

    if (this.product86LongPressTimer) {
      clearTimeout(this.product86LongPressTimer);
      this.product86LongPressTimer = null;
    }
    if (this.dejavooCartSendTimer) {
      clearTimeout(this.dejavooCartSendTimer);
      this.dejavooCartSendTimer = null;
    }
    window.removeEventListener("beforeunload", this.handleOrderBeforeUnload);
    window.removeEventListener("pagehide", this.handleOrderBeforeUnload);

    if (!this._skipDestroySync) {
      await this.forceQueueSync();
    }

    this.$root.$off('cd-pos-update', this.handleCdPosUpdate);

    clearInterval(this.syncing);
    window.removeEventListener("keypress", this.onKeyPress);

    // When navigating to the table-move picker, the Tables flow is about to
    // save the authoritative moved order. Saving this route's old currentOrder
    // here can re-queue the previous table on slow connections.
    if (this.$route.name === "TablesMoveOrder") {
      return;
    }

    // Persist open orders if the route changes.
    if ((this.currentOrder.local_id || this.currentOrder.id) && this.releaseLockedBy) {
      if (this.$route.name === "Tables") {
        this.$store.dispatch('toggleCallOrderList', true)
      }
    }
    // need to toggle it even if there is no order initiated
    if (this.$route.name === "Tables") {
      this.$store.dispatch('toggleCallOrderList', true)
    }
  },
  mounted() {
    window.addEventListener("beforeunload", this.handleOrderBeforeUnload);
    window.addEventListener("pagehide", this.handleOrderBeforeUnload);

    if (this.$route.params.id && this.$route.params.id !== "new") {
      this.checkForCompletedOrder(this.$route.params.id)
    }

    this.$root.$on('cd-pos-update', this.handleCdPosUpdate);

    this.releaseLockedBy = true
    const container = document.querySelector("#productWrapper");
    const self = this;
    if (container) {
      container.addEventListener("scroll", async function () {
        const userScrollHeight = container.scrollTop + container.clientHeight;
        const threshold = userScrollHeight + container.scrollHeight * 0.3;
        if (threshold - container.scrollHeight > -2 && self.pagination.page < self.pagination.pageCount) {
          self.pagination.page++;
          self.getProductListPaginated();
        }
      });
    }
    // todo fix remaining button from prev page
    window.addEventListener("keypress", this.onKeyPress);
    this.$root.$on("bv::modal::show", (bvEvent, modalId) => {
      switch (modalId) {
        case "enter-pin-modal":
        case "enter-pin-modal--order-discount":
          window.removeEventListener("keydown", this.onKeyItemDiscount);
          window.removeEventListener("keydown", this.onKeyQty);
          break;
      }
      window.removeEventListener("keypress", this.onKeyPress);
    });
    this.$root.$on("bv::modal::hide", (bvEvent, modalId) => {
      switch (modalId) {
        case "enter-pin-modal":
        case "enter-pin-modal--order-discount":
          window.addEventListener("keydown", this.onKeyItemDiscount);
          window.addEventListener("keydown", this.onKeyQty);
          break;
        case "modal-discount":
          window.removeEventListener("keydown", this.onKeyItemDiscount);
          break;
        case "modal-quantity":
          window.removeEventListener("keydown", this.onKeyQty);
          break;
        case "customer-modal":
        case "customer-modal-1":
          return window.removeEventListener("keypress", this.onKeyPress);
      }
      window.addEventListener("keypress", this.onKeyPress);
    });
  },
  methods: {
    // ─── Step 2 extraction notes / documented seams ─────────────────────────────
    // Pure, side-effect-free logic has been pulled into testable modules and is
    // delegated to from the thin wrappers below:
    //   • barcode SKU parsing      → @/helpers/order/barcode (checkForSKU)
    //   • readable text color      → @/helpers/ui/textColor (getReadableTextColor)
    //   • modifier group rule text → @/helpers/order/modifiers (getModifierGroupRules)
    //   • item/product display names → @/services/order/orderDomain
    //     (resolveItemDisplayName / resolveProductDisplayName / resolveSearchProductDisplayName)
    //   • order-item factory/grouping/dining-option → @/services/order/orderDomain
    //
    // The following remain in this component ON PURPOSE — they are tightly coupled
    // to component/Vuex state and the mature sync mechanics, so moving them would
    // risk behavior change on a live payment flow with no on-device QA:
    //   • the tax pipeline (calculateTaxes mixin) and anything that mutates the
    //     currentOrder proxy in place,
    //   • eager backend-queue sync via getOrderSync().schedule() and the
    //     orderBusinessSignature watcher,
    //   • payment/checkout orchestration (the isPaymentProcessing lock, etc.).
    // Additive integration: when the `cd_dejavoo` terminal setting is enabled, mirror the cart
    // to the first Dejavoo terminal configured on this POS after every cart change on this page.
    // This is independent of (does not override or cancel) the Customer Display broadcast above.
    sendCartToDejavooTerminal() {
      if (!isTerminalSettingEnabled(this.terminal, 'cd_dejavoo')) return;
      if (!this.currentOrder || !this.currentOrder.items) return;

      const status = this.currentOrder.status;
      if (status === ORDER_STATUS.COMPLETED ||
          status === ORDER_STATUS.CANCELLED ||
          status === ORDER_STATUS.REFUNDED) return;

      const dejavooPaymentType = (this.terminalPaymentTypes || [])
        .find((pt) => pt && pt.behavior === PAYMENT_BEHAVIOR.DEJAVOO_INTEGRATION && pt.is_at_pos);
      if (!dejavooPaymentType) return;

      if (this.dejavooCartSendTimer) clearTimeout(this.dejavooCartSendTimer);
      this.dejavooCartSendTimer = setTimeout(() => {
        const orderSource = `POS-${(this.terminal && (this.terminal.name || this.terminal.id)) || 'Unknown'}`;
        sendDejavooCart(this.currentOrder, this.products, dejavooPaymentType, orderSource)
          .catch((e) => console.warn('[Dejavoo] Failed to send cart to terminal', e));
      }, 300);
    },
    /**
     * FACTORY METHOD: Creates a standardized order item object.
     * Centralizes logic for ID generation, tax calculation, and default values.
     */
    buildOrderItem(product, options = {}) {
      // Steps 1–5 (price/identity/metadata + refund) now live in the order
      // domain service; tax calc (steps 6–7) stays here because it depends on
      // the tax mixins.
      const newItem = createBaseOrderItem(product, {
        quantity: options.quantity,
        isRefund: options.isRefund,
        price: options.price,
        customer: this.currentOrder.customer,
        employeeId: this.employee ? this.employee.id : null,
        terminalId: this.terminal.id,
        localId: this.getItemIterator(),
      });

      // 6. Calculate Taxes
      // We rely on component methods since they encapsulate the tax logic (Decimal.js etc)
      newItem.tax_items = this.calculateItemTaxItems(newItem);
      newItem.tax = calculateItemTaxTotal(newItem);

      // 7. Handle Tax Inclusive Pricing (Back-calculate price_before_tax)
      if (this.isTaxInclusive) {
        const totalTax = this.getItemAllTaxSum(newItem, true);
        newItem.price_before_tax = parseFloat(toFixed(newItem.price - totalTax, 2));
      }

      return newItem;
    },
    isGroupQtyEnabled() {
      // group_qty historically accepted both the string "true" and a real boolean.
      return isTerminalSettingEnabled(this.terminal, "group_qty", { acceptBoolean: true });
    },
    getItemModifierSignature(item) {
      return getItemModifierSignature(item);
    },
    getGroupableItemPrice(item) {
      return getGroupableItemPrice(item, { cashDiscount: !!(this.currentOrder && this.currentOrder.cash_discount) });
    },
    canGroupOrderItems(existingItem, newItem) {
      return canGroupOrderItems(existingItem, newItem, {
        groupQtyEnabled: this.isGroupQtyEnabled(),
        cashDiscount: !!(this.currentOrder && this.currentOrder.cash_discount),
      });
    },
    findGroupableOrderItemIndex(newItem, excludeIndex = -1) {
      if (!this.currentOrder || !Array.isArray(this.currentOrder.items)) return -1;
      return findGroupableOrderItemIndex(this.currentOrder.items, newItem, {
        groupQtyEnabled: this.isGroupQtyEnabled(),
        cashDiscount: !!(this.currentOrder && this.currentOrder.cash_discount),
        excludeIndex,
      });
    },
    recalculateOrderItem(index) {
      const item = this.currentOrder.items[index];
      item.subtotal = getItemSubtotal(item);
      item.grand_total = parseFloat(getItemGrandTotal(item).toFixed(2));
      item.tax_items = this.calculateItemTaxItems(item);
      item.tax = calculateItemTaxTotal(item) || 0;
    },
    mergeOrderItemQuantity(targetIndex, sourceItem) {
      const targetItem = this.currentOrder.items[targetIndex];
      targetItem.quantity = Number(targetItem.quantity || 0) + Number(sourceItem.quantity || 1);
      this.recalculateOrderItem(targetIndex);
      return targetItem;
    },
    tryGroupOrderItemAtIndex(sourceIndex) {
      const sourceItem = this.currentOrder?.items?.[sourceIndex];
      const targetIndex = this.findGroupableOrderItemIndex(sourceItem, sourceIndex);
      if (targetIndex === -1) return -1;

      this.mergeOrderItemQuantity(targetIndex, sourceItem);
      this.currentOrder.items.splice(sourceIndex, 1);
      return targetIndex;
    },
    async forceQueueSync() {
      // Immediate queue flush via the single OrderSyncCoordinator (replaces the
      // old queueSyncTimeout + manual enqueue/drain). The coordinator persists
      // locally, resolves a server id (create-blank) and then enqueues + drains.
      await this.getOrderSync().flush({ queue: true, trigger: "order_force_sync" });
    },
    handleOrderBeforeUnload() {
      this.releaseOrderPresence();
      this.forceQueueSync();
    },
    async releaseOrderPresence() {
      const orderId = this.activePresenceOrderId || this.currentOrder?.id || this.currentOrder?.local_id;
      this.activePresenceOrderId = null;

      if (!orderId || !liveObjectsService.isReady()) return;

      try {
        await liveObjectsService.leaveOrderPresence(orderId);
      } catch (err) {
        console.warn("Presence leave failed", err);
      }
    },
    waitForOrderIdBeforeQueueSync() {
      if (!this.currentOrder) return null;
      if (this.currentOrder.id) return this.currentOrder;

      const localId = this.currentOrder.local_id;
      if (!localId) return this.currentOrder;

      const resolvedOrder = this.$store.getters['orders/getOrderByIdOrLocalId'](localId);
      if (resolvedOrder) {
        this.currentOrder = resolvedOrder;
      }

      return this.currentOrder;
    },
    scheduleQueueSync() {
      // Debounced queue sync routed through the single OrderSyncCoordinator.
      this.getOrderSync().schedule({ queue: true, trigger: "order_force_sync" });
    },
    // Pure helpers extracted to testable modules (Step 2); kept as thin
    // instance methods so existing template / function-prop bindings are unchanged.
    getTextColor(hex) {
      return getReadableTextColor(hex);
    },
    modifierGroupRules (modifierGroup) {
      return getModifierGroupRules(modifierGroup);
    },
    ...mapActions("orders", [
      "setRefundItems",
      "setDeductRefundQuantity",
      "setEmptyCurrentOrder",
      "setRefundedOrderTotalDiscount",
      "fetchOrdersFromDb",
      "setOrderInitailSync",
    ]),
    ...mapActions("discount", ["setOrderDiscount"]),
    ...mapActions("config", [
      "fetchCategoriesFromDb",
      "fetchProductsFromDb",
      "fetchDiscountsFromDb",
      "fetchVoidReasonsFromDb",
      "fetchTablesFromDb",
    ]),
    ...mapActions("promotions", ["fetchPromotionsFromDb", "fetchPromotions"]),
    ...mapActions("giftVoucher", [
      "fetchGiftCardsAndPaymentsFromDb",
      "fetchGiftCardPayments",
      "fetchGiftCard",
      "setSelectedGiftCards",
    ]),
    async handleCdPosUpdate(payload) {
      if (!payload) return;
      const data = payload.data || payload;
      const { action, phone, tip_amount } = data;

      if (action === 'tip-update') {
        const orderId = data.order_id || data.local_id
        if ((this.currentOrder.id && this.currentOrder.id === orderId) || (this.currentOrder.local_id && this.currentOrder.local_id === orderId)) {
          this.$set(this.currentOrder, 'tips_total', tip_amount)
          await this.$store.dispatch("orders/saveOrder", this.currentOrder);
        }
      }

      if (action === 'add-customer' && phone) {
        const normalizePhone = (value) => {
          if (!value) return ''
          const digits = String(value).replace(/\D/g, '')
          if (digits.length === 11 && digits[0] === '1') return digits
          if (digits.length === 10) return `1${digits}`
          return digits
        }

        const targetDigits = normalizePhone(phone)
        const isPhoneMatch = (customerPhone) => {
          const customerDigits = normalizePhone(customerPhone)
          return !!customerDigits && customerDigits === targetDigits
        }

        let foundCustomer = null;

        try {
          const cached = await indexDbService.getDbData('customers');
          const cachedCustomers = Array.isArray(cached) ? cached : [];
          foundCustomer = cachedCustomers.find(c => isPhoneMatch(c.phone));
        } catch (e) {
          console.error(e)
        }

        if (!foundCustomer && this.isOnline) {
          try {
            const response = await DashboardService.getCustomersList({
              search: phone,
              page: 1
            })
            const results = (response && response.results) ? response.results : []
            foundCustomer = results.find(c => isPhoneMatch(c.phone)) || results[0] || null

            if (foundCustomer) {
              indexDbService.saveToDb('customers', foundCustomer).then()
              this.$store.commit('customers/save', foundCustomer)
            }
          } catch (e) {
            console.error(e)
          }
        }

        if (foundCustomer) {
          this.selectCustomer(foundCustomer);
        } else {
          // Check if customer_disable_name is enabled
          const isNameDisabled = isTerminalSettingEnabled(this.terminal, 'customer_disable_name');

          if (isNameDisabled) {
            // Parse phone number to extract country code and body
            let phoneValue = phone;
            // Ensure phone has country code prefix
            if (!phone.startsWith('+')) {
              // Assume US/Canada if no prefix
              const digitsOnly = phone.replace(/\D/g, '');
              if (digitsOnly.length === 10) {
                phoneValue = '+1' + digitsOnly;
              } else if (digitsOnly.length === 11 && digitsOnly.startsWith('1')) {
                phoneValue = '+' + digitsOnly;
              } else {
                phoneValue = '+1' + digitsOnly;
              }
            }

            // Generate customer name from last 4 digits of phone
            const phoneDigits = phoneValue.replace(/\D/g, '');
            const last4Digits = phoneDigits.slice(-4);
            const customerName = `Cust.${last4Digits}`;

            const newCustomer = {
              first_name: customerName,
              last_name: null,
              phone: phoneValue,
              email: null,
              notes: null,
              local_id: (new Date).toISOString(),
              addresses: [],
              default_address: null
            };

            this.$store.commit('customers/save', newCustomer);
            indexDbService.saveToDb('customers', newCustomer).then();
            this.selectCustomer(newCustomer);
          } else {
            // Open Customer Modal Simple with phone pre-filled
            this.$bvModal.show("customer-modal-simple");

            // Use a small delay to ensure modal is mounted/shown
            setTimeout(() => {
              // We need a way to pass data to the modal.
              // Since we can't easily pass props via bvModal.show, we might need to use an event bus
              // or find the component instance if possible, or use a store state.
              // For now, let's try emitting an event that CustomerModalSimple listens to,
              // or modifying the modal to check a global/store value.

              // Actually, best way is to emit an event on root that the modal listens to
              this.$root.$emit('set-customer-modal-phone', phone);
              this.$root.$emit('focus-customer-modal-input');
            }, 200);
          }
        }
      }
    },
    onQuantitySelected(qty) {
      this.selectedQuantityFromBar = qty;
    },
    showNotesModal (item) {
      let index = -1;

      // If we received an object (item), find its index by ID
      if (typeof item === 'object' && item !== null) {
        index = this.currentOrder.items.findIndex(i => i.id === item.id);
      } else {
        // Fallback for legacy calls passing index directly
        index = item;
      }

      // Check if item exists before opening modal
      if (index === -1 || !this.currentOrder.items[index]) {
        console.warn('Cannot open notes modal - item not found. Input:', item, 'Resolved index:', index);
        return;
      }

      this.$store.commit("itemNote/setIndex", index);
      this.$store.commit("itemNote/setNotes", this.currentOrder.items[index].notes);
      this.$bvModal.show("modal-note-order-item");
    },
    showPriceAdjustModal (item) {
      this.$store.commit("itemPriceAdjust/setItem", item);
      const index = this.currentOrder.items.findIndex(i => i.local_id === item.local_id);

      // Check if item exists before opening modal
      if (index === -1 || !this.currentOrder.items[index]) {
        console.warn('Cannot open price adjust modal - item not found:', item.local_id);
        return;
      }

      this.$store.commit("itemPriceAdjust/setIndex", index);
      this.$store.commit("itemPriceAdjust/setPrice", this.currentOrder.items[index].price);
      this.$bvModal.show("modal-price-adjust");
    },
    showDiningOptionsModal () {
      return this.$bvModal.show("dining-options-modal");
    },
    selectDiningOption (optionId) {
      this.currentOrder.dining_option_type = optionId;
      this.currentOrder.dining_option = this.orderDiningOption.name;
      // this.sync();
    },
    async selectCustomer (customer) {
      // If we're on the 'new' order page, create the order after selecting a customer.
      // Use initNewOrder (not the local-only createNewOrder) so the order is
      // persisted to the store AND created on the backend right away — otherwise
      // a customer-first order has no server id until the first product is added.
      // Mirrors the product path (processSingleItem) and TableOrders.vue.
      if (this.$route.params.id === "new") {
        const newOrder = await this.$store.dispatch("orders/initNewOrder", {
          related_terminal: this.terminal.id,
          table: this.$route.params.tableID || undefined,
        });
        this.currentOrder = newOrder;

        // Apply default dining option + TABLE_SERVICE override (shared service).
        // onlyIfUnset:false matches the previous createNewOrder behavior here.
        applyDiningOption(this.currentOrder, {
          diningOptions: this.terminal["dining_options"],
          defaultDiningOption: this.default_dining_option,
          isTableService: this.terminalMode === "TABLE_SERVICE",
          onlyIfUnset: false,
        });

        this.$store.dispatch("orders/saveOrder", this.currentOrder);

        // initNewOrder already dispatched orders/createRemoteOrder internally.

        // Assign customer to the order (this will also call sync)
        await this.assignCustomerToOrder(customer);

        // Navigate to the new order page
        router.replace({ name: "Order", params: { id: this.currentOrder.id || this.currentOrder.local_id } });
        return;
      }

      return this.assignCustomerToOrder(customer);
    },
    async selectEmployeeForDiscount (employee) {
      // Mirrors selectCustomer: create the order first if we're on the 'new' page.
      if (this.$route.params.id === "new") {
        const newOrder = await this.$store.dispatch("orders/initNewOrder", {
          related_terminal: this.terminal.id,
          table: this.$route.params.tableID || undefined,
        });
        this.currentOrder = newOrder;

        applyDiningOption(this.currentOrder, {
          diningOptions: this.terminal["dining_options"],
          defaultDiningOption: this.default_dining_option,
          isTableService: this.terminalMode === "TABLE_SERVICE",
          onlyIfUnset: false,
        });

        this.$store.dispatch("orders/saveOrder", this.currentOrder);

        this.assignEmployeeToOrder(employee);

        router.replace({ name: "Order", params: { id: this.currentOrder.id || this.currentOrder.local_id } });
        return;
      }

      return this.assignEmployeeToOrder(employee);
    },
    async showSelectCustomerModal () {
      // Check if add_customer_direct setting is enabled
      const isDirectMode = isTerminalSettingEnabled(this.terminal, 'add_customer_direct');

      if (isDirectMode && !this.currentOrder.customer) {
        // Directly open the simplified customer modal
        return this.$bvModal.show("customer-modal-simple");
      }

      if (this.isOnline) {
        this.isLoad = true;
        try {
          await this.$store.dispatch("customers/fetch");
        } catch (e) {
          console.error(e);
        }
        this.isLoad = false;
      }
      return this.$bvModal.show("select-customer-modal");
    },
    onKeyPress() {
      // todo, outside
      if (this.$refs.orderSearchBar) {
        this.$refs.orderSearchBar.focusSearchField();
      }
    },
    async findProduct () {
      if (this.search.value === "") {
        this.search.visible = false;
      }
      else if (isMembershipScanValue(this.search.value) && isTerminalSettingEnabled(this.terminal, 'eos_memberscan')) {
        const scannedCode = this.search.value;
        this.search.value = "";
        this.search.visible = false;
        const resolvedCustomer = await resolveMemberScanCustomer(scannedCode);
        this.selectCustomer(resolvedCustomer);
      }
      else {
        let searchValue = 0
        //first search for products with whole barcode without breaking it done
        let products = this.searchProductByName(this.search.value);
        if(products.length === 0){
          // if the whole barcode is not found than break it down for SKU
          searchValue = this.checkForSKU(this.search.value)
          products = this.searchProductByName(searchValue?.value);
        }

        const product = products.length > 0 ? products[0] : null;

        // Only check for order if receipt search is enabled
        const numericValue = parseInt(this.search.value);
        let order = null;
        if (this.isReceiptSearchEnabled && !isNaN(numericValue) && this.search.value === numericValue.toString()) {
          // Check if order exists in local store
          order = this.$store.getters["orders/getOrderByIdOrLocalId"](numericValue);

          // If not found locally, try fetching from backend
          if (!order) {
            try {
              this.isLoad = true;
              order = await this.$store.dispatch("orders/fetchOrderById", numericValue);
              this.isLoad = false;
            } catch (error) {
              this.isLoad = false;
              order = null;
            }
          }
        }

        // Now decide what to do based on what was found
        if (product && order) {
          // Both found - show combined dialog
          await this.showProductAndOrderFoundDialog(product, order, searchValue);
        } else if (product) {
          // Only product found - add to current order
          const selectedProduct = {...product}
          if(searchValue.price)
          {
            selectedProduct.price = searchValue.price
            selectedProduct.price_base = searchValue.price
          }
          if (!this.isKioskMode && this.isProduct86d(selectedProduct.id)) {
            this.search.value = "";
            this.search.visible = false;
            this.showProduct86UnavailableModal();
            return;
          }
          this.addItemToOrder(selectedProduct);
        } else if (order) {
          // Only order found - navigate to order
          await this.searchOrderById(this.search.value);
        } else {
          // Nothing found - show error
          this.search.value = "";
          this.search.visible = false;
          const sound = new Audio(require("@/assets/sounds/error-sound.mp3"));
          sound.play();
          this.$bvModal.show("modal-product-not-found");
        }
      }
    },
    async searchOrderById(searchValue) {
      // Check if search value is numeric (order ID)
      const orderId = parseInt(searchValue);
      if (isNaN(orderId)) {
        // Not a valid order ID, show product not found
        this.search.value = "";
        this.search.visible = false;
        const sound = new Audio(require("@/assets/sounds/error-sound.mp3"));
        sound.play();
        this.$bvModal.show("modal-product-not-found");
        return;
      }

      try {
        // First check if order exists in local store
        let order = this.$store.getters["orders/getOrderByIdOrLocalId"](orderId);

        // If not found locally, try fetching from backend
        if (!order) {
          this.isLoad = true;
          order = await this.$store.dispatch("orders/fetchOrderById", orderId);
          this.isLoad = false;
        }

        if (order && order.id) {
          // Check if order is closed, cancelled, or refunded - navigate to orders list view
          const isClosedOrder = order.status === ORDER_STATUS.COMPLETED ||
                                order.status === ORDER_STATUS.CANCELLED ||
                                order.status === ORDER_STATUS.REFUNDED;

          if (isClosedOrder) {
            // Clear search
            this.search.value = "";
            this.search.visible = false;
            this.search.results = [];

            // Show message and navigate to orders list
            const self = this;
            const statusText = order.status === ORDER_STATUS.COMPLETED ? 'Completed' :
                              order.status === ORDER_STATUS.CANCELLED ? 'Cancelled' : 'Refunded';

            const messageVNode = this.$createElement("div", {
              domProps: {
                innerHTML: `
                  <div class="success-popup-body">
                    <svg class="icon icon-icon-check-circle" style="width: 48px; height: 48px; color: #28a745;">
                      <use xlink:href="#icon-icon-check-circle" />
                    </svg>
                    <h5>Order #${order.number || order.id} Found</h5>
                    <p>This order is ${statusText}. Navigating to orders list...</p>
                  </div>
                `
              }
            });

            this.$bvModal.msgBoxOk([messageVNode], {
              title: [],
              centered: true,
              modalClass: "success-popup",
              hideHeader: true,
              footerClass: "d-flex justify-content-center align-items-center",
              okVariant: "primary",
              okTitle: "OK",
              noFade: true
            }).then(function () {
              // Navigate to orders list with order ID as query param
              self.$router.push({
                name: "OrdersList",
                query: { orderId: order.id }
              });
            });
            return;
          }

          // Check if we're already on this order
          const currentOrderId = this.currentOrder.id || this.currentOrder.local_id;
          const targetOrderId = order.id || order.local_id;

          if (currentOrderId === targetOrderId) {
            // Already on this order - just clear search and show message
            this.search.value = "";
            this.search.visible = false;
            this.search.results = [];

            const messageVNode = this.$createElement("div", {
              domProps: {
                innerHTML: `
                  <div class="success-popup-body">
                    <h5>Already on Order #${order.number || order.id}</h5>
                    <p>You are already viewing this order.</p>
                  </div>
                `
              }
            });

            this.$bvModal.msgBoxOk([messageVNode], {
              title: [],
              centered: true,
              modalClass: "success-popup",
              hideHeader: true,
              footerClass: "d-flex justify-content-center align-items-center",
              okVariant: "primary",
              okTitle: "OK",
              noFade: true
            });
            return;
          }

          // Order found - check if current order has items
          const hasItemsInCurrentOrder = this.currentOrder.items &&
                                         this.currentOrder.items.filter(item => item.void_status !== 1).length > 0;

          this.search.value = "";
          this.search.visible = false;

          if (hasItemsInCurrentOrder) {
            // Ask for confirmation before navigating
            const self = this;
            const messageVNode = this.$createElement("div", {
              domProps: {
                innerHTML: `
                  <div class="success-popup-body">
                    <h5>Order #${order.number || order.id} Found</h5>
                    <p>You have items in the current order. Are you sure you want to navigate to order #${order.number || order.id}?</p>
                    <p class="text-muted small">Current order will be saved automatically.</p>
                  </div>
                `
              }
            });

            this.$bvModal.msgBoxConfirm([messageVNode], {
              title: [],
              centered: true,
              modalClass: "success-popup",
              hideHeader: true,
              footerClass: "d-flex justify-content-center align-items-center",
              cancelVariant: "outline-secondary",
              okVariant: "primary",
              okTitle: "YES, NAVIGATE",
              cancelTitle: "CANCEL",
              noFade: true
            }).then(async function (result) {
              if (result) {
                // User confirmed - clear search and navigate to the order
                self.search.value = "";
                self.search.visible = false;
                self.search.results = [];
                self.$router.push({ name: "Order", params: { id: order.id } });
              }
            });
          } else {
            // No items in current order - show info and navigate
            const self = this;
            const messageVNode = this.$createElement("div", {
              domProps: {
                innerHTML: `
                  <div class="success-popup-body">
                    <svg class="icon icon-icon-check-circle" style="width: 48px; height: 48px; color: #28a745;">
                      <use xlink:href="#icon-icon-check-circle" />
                    </svg>
                    <h5>Order #${order.number || order.id} Found</h5>
                    <p>Navigating to the order...</p>
                  </div>
                `
              }
            });

            this.$bvModal.msgBoxOk([messageVNode], {
              title: [],
              centered: true,
              modalClass: "success-popup",
              hideHeader: true,
              footerClass: "d-flex justify-content-center align-items-center",
              okVariant: "primary",
              okTitle: "OK",
              noFade: true
            }).then(function () {
              // Clear search and navigate to the order
              self.search.value = "";
              self.search.visible = false;
              self.search.results = [];
              self.$router.push({ name: "Order", params: { id: order.id } });
            });
          }
        } else {
          // Order not found
          this.search.value = "";
          this.search.visible = false;
          const sound = new Audio(require("@/assets/sounds/error-sound.mp3"));
          sound.play();
          this.$bvModal.show("modal-product-not-found");
        }
      } catch (error) {
        console.error("[searchOrderById] Error searching for order:", error);
        this.isLoad = false;
        this.search.value = "";
        this.search.visible = false;
        const sound = new Audio(require("@/assets/sounds/error-sound.mp3"));
        sound.play();
        this.$bvModal.show("modal-product-not-found");
      }
    },
    async showProductAndOrderFoundDialog(product, order, searchValue) {
      // Both product and order found - show combined dialog
      const self = this;
      const messageVNode = this.$createElement("div", {
        domProps: {
          innerHTML: `
            <div class="success-popup-body">
              <h5>Product & Order Found</h5>
              <p><strong>Product:</strong> ${product.name} (${this.currency}${product.price.toFixed(2)})</p>
              <p><strong>Order:</strong> #${order.number || order.id}</p>
              <p>What would you like to do?</p>
            </div>
          `
        }
      });

      this.$bvModal.msgBoxConfirm([messageVNode], {
        title: [],
        centered: true,
        modalClass: "success-popup",
        hideHeader: true,
        footerClass: "d-flex justify-content-center align-items-center",
        okVariant: "primary",
        okTitle: "ADD PRODUCT TO CURRENT ORDER",
        cancelVariant: "outline-primary",
        cancelTitle: "GO TO ORDER #" + (order.number || order.id),
        noFade: true
      }).then(function (result) {
        self.search.value = "";
        self.search.visible = false;
        if (result) {
          // Add product to current order
          const selectedProduct = {...product}
          if(searchValue.price)
          {
            selectedProduct.price = searchValue.price
            selectedProduct.price_base = searchValue.price
          }
          self.addItemToOrder(selectedProduct);
        } else {
          // Navigate to the order
          self.searchOrderById(order.id || order.local_id);
        }
      }).catch(function () {
        self.search.value = "";
        self.search.visible = false;
      });
    },

    checkForSKU(barcode){
      // Pure barcode/SKU parser extracted to helpers/order/barcode (Step 2).
      return checkForSKU(barcode);
    },
    onKeyItemDiscount (event) {
      this.typeDiscount(this.getNum(event));
    },
    getNum (event) {
      let num = 0;
      if (event.keyCode >= 48 && event.keyCode <= 57) {
        num = event.keyCode - 48;
      } else if (event.keyCode === 8) {
        num = "C";
      }

      return num;
    },
    init: async function () {
      if (this.$route.params.diningOption) {
        this.currentOrder.dining_option_type = this.$route.params.diningOption;
      }
      if (!this.terminal) {
        console.error("No terminal at init order stage", this.terminal);
      }

      // set terminal of the new order initiated
      this.currentOrder.related_terminal = this.terminal ? this.terminal.id : null;

      // set dinning options (default + TABLE_SERVICE override, shared service)
      applyDiningOption(this.currentOrder, {
        diningOptions: this.terminal["dining_options"],
        defaultDiningOption: this.default_dining_option,
        isTableService: this.terminalMode === "TABLE_SERVICE",
      });

      // if the order exists then load the order from the state else set table id, if present in routes
      if (this.$route.params.id && this.$route.params.id !== "new") {
        const orderKey = this.$route.params.id.toString().includes("_") ? this.$route.params.id
          : parseInt(this.$route.params.id);
        await this.currentOrderInit(orderKey);
      } else {
        this.currentOrder.table = this.$route.params.tableID ? this.$route.params.tableID : this.currentOrder.table;

        // Check if customer_prompt setting is enabled and we're on the 'new' order page
        if (this.$route.params.id === "new") {
          if (isTerminalSettingEnabled(this.terminal, 'customer_prompt')) {
            this.$nextTick(() => {
              this.showSelectCustomerModal();
            });
          }

          // Check if productId query parameter exists and add that product to the order
          if (this.$route.query.productId) {
            const productId = parseInt(this.$route.query.productId);
            const product = this.products.find(p => p.id === productId);
            if (product) {
              this.$nextTick(() => {
                this.addItemToOrder(product);
              });
            }
          }
        }
      }

      // Resync the Dejavoo terminal (if cd_dejavoo is enabled) every time this page loads
      // an order for editing - a fresh order, an existing one, or switching between orders.
      this.sendCartToDejavooTerminal();
    },
    async currentOrderInit(orderID) {
      this.isInitializing = true;

      const orderKey = orderID.toString().includes("_") ? orderID
          : parseInt(orderID);
      let orderFromStore = this.$store.getters["orders/getOrderByIdOrLocalId"](orderKey);

      if (!orderFromStore || !orderFromStore.id && !orderFromStore.local_id) {
        console.error("Order not found for id:", orderKey);
        this.$store.dispatch("errorModal/displayModalError", {
          modalId: "order-error",
          errorBody: {
            title: "Order not found",
            info: `Order #${orderID} was not found. Please check if the order exists.`
          }
        });
        if (this.$route.params.tableID) {
          return this.$router.push({name: "TableOrders", params: {id: this.$route.params.tableID}}).catch(e => console.warn("Navigation error:", e));
        } else {
          return this.$router.push({name: "OrdersList"}).catch(e => console.warn("Navigation error:", e));
        }
      }

      // Bind the proxy to the live store order (single source of truth). The
      // mutations below (printer hydration, discount normalisation, countSubtotal)
      // now land directly on the Vuex object instead of a detached copy.
      this.currentOrder = orderFromStore;

      if (!this.currentOrder.items || !Array.isArray(this.currentOrder.items)) {
        this.currentOrder.items = [];
      }

      this.currentOrder.items = this.currentOrder.items.map(item => {
        const productConfig = this.products.find(product => product.id === item.product)
        if (productConfig && productConfig.printers && productConfig.printers.length > 0) {
          item.printers = productConfig.printers
        }
        return item
      });

      // Convert any line-style item discounts to the POS per-unit convention
      // now, before countSubtotal() below rebuilds discount_total/grand_total
      // from the items - otherwise a line discount would be multiplied by
      // quantity again and inflate the totals.
      const discountNormalizedOrder = normalizeOrderDiscounts(this.currentOrder);
      if (discountNormalizedOrder !== this.currentOrder) {
        this.currentOrder.items = discountNormalizedOrder.items;
      }

      // Since we load locally, keep Vuex/IndexedDB aligned before checking
      // whether this order needs a backend refresh.
      if (this.currentOrder.id) {
        this.$store.dispatch("orders/saveOrder", this.currentOrder);
      }

      const TWELVE_HOURS_MS = 12 * 60 * 60 * 1000;
      const orderUpdatedAt = orderFromStore.updated_at ? new Date(orderFromStore.updated_at).getTime() : 0;
      const isOrderFresh = (Date.now() - orderUpdatedAt) < TWELVE_HOURS_MS;
      const hasCompleteData = Array.isArray(orderFromStore.items) && orderFromStore.items.length > 0;

      if (orderFromStore.id && window.navigator.onLine && !(isOrderFresh && hasCompleteData)) {
        this.$store.dispatch("orders/fetchOrderById", orderFromStore.id)
            .then(completeOrder => {
              if (completeOrder && Array.isArray(completeOrder.items) && completeOrder.items.length > 0) {
                this.$store.dispatch("orders/saveOrder", completeOrder);
              }
            })
            .catch(error => {
              console.error("[Background Fetch] Error fetching order details:", error);
            });
      }

      if (this.currentOrder.status === ORDER_STATUS.COMPLETED) {
        this.$store.dispatch("errorModal/displayModalError", {
          modalId: "order-error",
          errorBody: {
            title: "Order you are trying to open is completed",
            info: `You can edit only Open orders...`
          }
        });
      }

      for (const item of this.currentOrder.items) {
        const product = this.products.find((prod) => prod.id == item.product);
        if (!product) {
          this.$store.dispatch("errorModal/displayModalError", {
            modalId: "order-error",
            errorBody: {
              title: "Product not found",
              info: `Please, check that product with id "${item.product}" is active and available for current store`
            }
          });
          throw Error(`Not found product with id ${item.product}`);
        }
      }

      this.activePresenceOrderId = this.currentOrder.id || this.currentOrder.local_id;
      if (this.activePresenceOrderId && liveObjectsService.isReady()) {
        liveObjectsService.enterOrderPresence(this.activePresenceOrderId).catch(err => console.warn("Presence failed", err));
      }

      const reevaluatedOnlinePromotions = await this.reEvaluateImportedOnlineOrderPromotions();
      if (!reevaluatedOnlinePromotions) {
        await this.countSubtotal();
      }
      this.discount = {val: "", changed: false, type: 1};

      if (this.refundOrderId && !this.$route.params.tableID) {
        this.currentOrder.table = this.refundedOrderTableId
      } else {
        this.currentOrder.table = this.$route.params.tableID ? this.$route.params.tableID : this.currentOrder.table;
      }

      // @Deprecated - sync is now called in the watcher for currentOrder with debounce, so we don't need to call it here explicitly
      // this.sync(true, false).catch(err => console.warn("Init sync failed", err));

      this.$nextTick(() => {
        this.isInitializing = false;
      });
    },
    saveNote (noteReturnObj) {
      const itemIndex = this.$store.getters["itemNote/index"];
      const item = this.currentOrder.items[itemIndex];

      // Check if item still exists (could have been removed/voided while modal was open)
      if (!item) {
        console.warn('Item no longer exists at index', itemIndex, '- cannot save notes');
        return;
      }

      item.notes = noteReturnObj.notes;
      this.scheduleOrderSync();
    },
    async savePrice(returnObj) {
      const index = this.$store.getters["itemPriceAdjust/index"];

      if (index === null || !this.currentOrder.items[index]) {
        console.warn('[savePrice] Item no longer exists at index', index);
        return;
      }

      let currentOrderitems = [...this.currentOrder.items];

      let itemToUpdate = currentOrderitems[index];

      itemToUpdate.price = returnObj.price;
      itemToUpdate.price_base = returnObj.price;

      // --- FIX: ---
      itemToUpdate.discount = 0;
      if (itemToUpdate.discount_percent) itemToUpdate.discount_percent = 0;
      if (itemToUpdate.wholeDiscount) itemToUpdate.wholeDiscount = 0;
      // ------------------------------------

      itemToUpdate["tax_items"] = this.calculateItemTaxItems(itemToUpdate);

      if (this.isTaxInclusive) {
        itemToUpdate.price_before_tax = parseFloat(toFixed(returnObj.price - this.getItemAllTaxSum(itemToUpdate, true), 2));
      } else {
        itemToUpdate.price_before_tax = parseFloat(toFixed(returnObj.price, 2));
      }

      itemToUpdate.tax_status = returnObj.taxed ? 0 : 1;
      itemToUpdate.tax = returnObj.taxed ? this.getItemAllTaxSum(itemToUpdate) : 0;

      itemToUpdate.subtotal = getItemSubtotal(itemToUpdate);

      itemToUpdate.grand_total = parseFloat((getItemGrandTotal(itemToUpdate, this.store.tax_option === TAX_OPTION.TAX_INCLUSIVE)).toFixed(2));

      this.currentOrder.items = [...currentOrderitems];

      this.countSubtotal();
    },
    hideSearch () {
      this.search.visible = false;
      this.search.canScrollUp = false;
      this.search.canScrollDown = false;
    },
    onInputChangeSearch (input) {
      this.search.value = input;
      this.doSearch();
    },
    doSearch () {
      if (this.search.value.length > 0) {
        this.search.visible = true;
        if (this.search.value.length > 1) {
          // The OrderSearchBar child re-runs its scroll-state check via a
          // watcher on search.results, so we no longer call checkSearchScroll here.
          this.search.results = this.searchProductByName(this.search.value);
        }
      } else {
        this.search.visible = false;
        this.search.results = [];
        this.search.canScrollUp = false;
        this.search.canScrollDown = false;
      }
    },
    sidebarMobToggle () {
      this.sidebarMob = !this.sidebarMob;
    },
    taxedToggle () {
      this.isTaxed = !this.isTaxed;
    },
    createNewOrder () {
      this.currentOrder.locked_by = this.terminal ? this.terminal.id : null;
      this.currentOrder.local_id = `${this.terminal.id}_${Date.now()}`;
      this.currentOrder.open_date = new Date().toISOString();
      this.currentOrder.emp_open = this.employee ? this.employee.id : null;
      this.currentOrder.updated_at = new Date().toISOString();
      // createNewOrder applies the default unconditionally (onlyIfUnset: false).
      applyDiningOption(this.currentOrder, {
        diningOptions: this.terminal["dining_options"],
        defaultDiningOption: this.default_dining_option,
        isTableService: this.terminalMode === "TABLE_SERVICE",
        onlyIfUnset: false,
      });
    },
    resetSearch: function () {
      this.search = {
        visible: false,
        value: "",
        results: []
      };
      this.hideBarcodeNumpad();
    },
    applyProductCategoryFilter () {
      const value = this.currentCategory;
      this.filteredProductsByCategory = this.products.filter(item => {
        if (Number(item.id) === this.giftCardProductId || item.id === -3 || item.name === 'Points Redeemed') return false;
        if (this.isKioskMode) {
          if (!item.categories.includes(value) || !item.order_kiosk) return false;
          if (this.isProduct86d(item.id)) return false;
          return true;
        }
        return item.categories.includes(value);
      }).sort(((a, b) => a.sorting - b.sorting || a.name.localeCompare(b.name)));
      this.pagination.pageCount = Math.ceil(this.filteredProductsByCategory.length / this.pagination.perPage);
      this.paginatedProducts = [];
      this.pagination.page = 1;
      this.getProductListPaginated();
    },
    onProductPressStart (product, e) {
      if (this.isKioskMode) return;
      if (!this.isProduct86ControlsEnabled) return;
      const t = e && e.target;
      if (t && t.closest && t.closest(".products-info")) return;
      if (this.product86LongPressTimer) {
        clearTimeout(this.product86LongPressTimer);
        this.product86LongPressTimer = null;
      }
      this.product86LongPressConsumed = false;
      this.product86LongPressTimer = setTimeout(() => {
        this.product86LongPressTimer = null;
        this.product86LongPressConsumed = true;
        this.product86MenuProduct = product;
        this.product86ToggleValue = this.isProduct86d(product.id);
        this.$bvModal.show("modal-product-86");
      }, 700);
    },
    onProductPressEnd () {
      if (this.product86LongPressTimer) {
        clearTimeout(this.product86LongPressTimer);
        this.product86LongPressTimer = null;
      }
    },
    onProductTileClick (product) {
      if (this.product86LongPressConsumed) {
        this.product86LongPressConsumed = false;
        return;
      }
      if (!this.isKioskMode && this.isProduct86d(product.id)) {
        this.showProduct86UnavailableModal();
        return;
      }
      this.addItemToOrder(product);
    },
    async onProduct86ToggleChange () {
      if (!this.isProduct86ControlsEnabled) return;
      if (!this.product86MenuProduct) return;
      const requestedValue = this.product86ToggleValue;
      try {
        await this.$store.dispatch("products86d/setAndPublish", {
          productId: this.product86MenuProduct.id,
          is86d: requestedValue
        });
        this.product86ToggleValue = this.isProduct86d(this.product86MenuProduct.id);
      } catch (e) {
        console.error("[products86d] Failed to update product availability", e);
        this.product86ToggleValue = this.isProduct86d(this.product86MenuProduct.id);
        this.$store.dispatch("errorModal/displayModalError", {
          modalId: "order-error",
          errorBody: {
            title: "Availability update failed",
            info: "Could not update product availability. Please check your connection and try again."
          }
        });
      }
    },
    async onModifier86Toggle (modifierOptionId, checked) {
      if (!this.isProduct86ControlsEnabled) return;
      if (!this.product86MenuProduct) return;
      try {
        await this.$store.dispatch("products86d/setModifierAndPublish", {
          productId: this.product86MenuProduct.id,
          modifierOptionId,
          is86d: checked
        });
      } catch (e) {
        console.error("[products86d] Failed to update modifier availability", e);
        this.$store.dispatch("errorModal/displayModalError", {
          modalId: "order-error",
          errorBody: {
            title: "Availability update failed",
            info: "Could not update modifier availability. Please check your connection and try again."
          }
        });
      }
    },
    onProduct86ModalHidden () {
      this.product86MenuProduct = null;
      this.product86ToggleValue = false;
    },
    showProduct86UnavailableModal () {
      const info = this.isProduct86ControlsEnabled
        ? "This item is out of stock (86). Long-press the product tile to open Product availability and mark it back in stock."
        : "This item is out of stock (86).";
      this.$store.dispatch("errorModal/displayModalError", {
        modalId: "order-error",
        errorBody: {
          title: "Unavailable",
          info
        }
      });
    },
    addItemToOrder: function (product) {
      this.itemQueue.push(product);
      if (!this.isAddingItem) {
        this.processItemQueue();
      }
    },
    async processItemQueue() {
      if (this.isAddingItem) return;
      this.isAddingItem = true;

      try {
        // Process all items currently in the queue locally
        while (this.itemQueue.length > 0) {
          const nextProduct = this.itemQueue.shift();
          // We await the LOCAL processing (calculations), but removed network calls inside
          await this.processSingleItem(nextProduct);
        }
      } catch (e) {
        console.error("Queue processing error:", e);
      } finally {
        this.isAddingItem = false;

        // OPTIMISTIC UI FIX:
        // Trigger sync ONLY after the immediate burst of items is processed locally.
        // This ensures popups/UI update instantly, and network sync happens in the background.
        this.scheduleOrderSync();
      }
    },
    processSingleItem: async function (product) {
      if (this.isProduct86d(product.id)) {
        if (!this.isKioskMode) {
          this.showProduct86UnavailableModal();
        }
        return;
      }
      try {
        this.bufferHelper.itemsSavingCounter++;
        this.resetSearch();

        // FIX: Check for local context (local_id) to prevent overwriting an order
        // that was just initialized (e.g. by Select Customer) but hasn't received a Remote ID yet.
        const hasLocalContext = this.currentOrder && this.currentOrder.local_id && this.currentOrder.local_id !== 0;

        // Only init new order if we are on 'new' route AND have no ID AND no local context
        if ((this.$route.params.id === "new" && !this.currentOrder?.id && !hasLocalContext)
            || !this.currentOrder) {
          const newOrder = await this.$store.dispatch('orders/initNewOrder',{
            related_terminal: this.terminal.id,
            table: this.$route.params.tableID || undefined,
          });
          this.currentOrder = newOrder;

          // Apply default dining option + TABLE_SERVICE override (shared service)
          applyDiningOption(this.currentOrder, {
            diningOptions: this.terminal["dining_options"],
            defaultDiningOption: this.default_dining_option,
            isTableService: this.terminalMode === "TABLE_SERVICE",
          });

          this.$store.dispatch("orders/saveOrder", this.currentOrder);

          if (this.$route.params.id === "new") {
            router.replace({ name: "Order", params: { id: this.currentOrder.id || this.currentOrder.local_id, tableID: this.$route.params.tableID } });
          }
        }

        // Handle Points Redeemed locally
        if (product && product.name === 'Points Redeemed') {
          if (!this.currentOrder.customer) {
            this.$store.dispatch("errorModal/displayModalError", {
              modalId: "order-error",
              errorBody: {
                title: "Select a customer",
                info: "Please select a customer before redeeming points."
              }
            });
            return;
          }

          const pointsToAdd = Math.abs(this.selectedQuantityFromBar || product.quantity || 1);
          const existingItem = this.currentOrder.items.find(item => item.name === 'Points Redeemed' && !item.void_status);
          const currentRedeemedPoints = existingItem ? Math.abs(existingItem.quantity || 0) : 0;

          if (this.selectedQuantityFromBar) {
            this.selectedQuantityFromBar = null;
            if (this.$refs.quantityBarDesktop) this.$refs.quantityBarDesktop.resetQuantity();
            if (this.$refs.quantityBarMobile) this.$refs.quantityBarMobile.resetQuantity();
          }

          await this.savePointsRedemption(currentRedeemedPoints + pointsToAdd);
          return;
        }

        // Setup Item Data
        this.currentOrder.isActive = true;
        this.currentOrder.updated_at = new Date().toISOString();
        this.currentOrder.terminal_id = this.terminal.id;
        let itemPrice = parseFloat(toFixed(product.refundedPrice? product.refundedPrice :product.price, 2));

        if (product.special_prices_items.length && this.currentOrder.customer && this.currentOrder.customer.group) {
          product.special_prices_items.map(specialPrice => {
            if (specialPrice.group !== this.currentOrder.customer.group) return;
            itemPrice = specialPrice.price;
          });
        }

        const isRefundContext = !!(product.refundItemId || this.$store.getters['orders/refundOrderId']);

        const newIndex = this.currentOrder.items.length;

        const newItem = this.buildOrderItem(product, {
          quantity: this.selectedQuantityFromBar || product.quantity,
          isRefund: !!product.refundItemId
        });

        if (this.selectedQuantityFromBar) {
          this.selectedQuantityFromBar = null;
          if (this.$refs.quantityBarDesktop) this.$refs.quantityBarDesktop.resetQuantity();
          if (this.$refs.quantityBarMobile) this.$refs.quantityBarMobile.resetQuantity();
        }

        if (product.refundItemId) {
          newItem.order_refund_item = product.refundItemId;
          newItem.tax_status = product.refundItemTax_status;
          if (this.refundedOrderDiscountTotalDetails.value && this.refundedOrderDiscountTotalDetails.value !== 0) {
            this.currentOrder.discount_orders_raw_value = this.refundedOrderDiscountTotalDetails.value;
            this.currentOrder.discount_orders_raw_type = this.refundedOrderDiscountTotalDetails.discountType;
            this.currentOrder.discount_orders = this.refundedOrderDiscountTotalDetails.value;
          }
        }
        if (product.refundItemId) newItem.void_item = 0;

        if (!product["modifier_groups"].length && !product["price_adjust"] && !product["qty_adjust"]) {
          const groupedItemIndex = this.findGroupableOrderItemIndex(newItem);
          if (groupedItemIndex !== -1) {
            this.mergeOrderItemQuantity(groupedItemIndex, newItem);
            await this.reEvaluatePromotions();
            await this.countSubtotal();
            this.currentOrder.balance_remaining = Math.max(0, this.currentOrderSum);

            if (this.currentOrder) {
              this.currentOrder.updated_at = new Date().toISOString();
              this.$store.dispatch('orders/saveOrder', this.currentOrder);
            }
            return;
          }
        }

        // Update State
        this.currentOrder.items.push(newItem);

        // Calculate Taxes Locally (Decimal.js)
        this.currentOrder.items[newIndex].tax_items = this.calculateItemTaxItems(newItem);
        this.currentOrder.items[newIndex].tax = calculateItemTaxTotal(this.currentOrder.items[newIndex]);

        if (this.isTaxInclusive) {
          this.currentOrder.items[newIndex].price = itemPrice;
          this.currentOrder.items[newIndex].price_before_tax = parseFloat(toFixed(itemPrice - this.getItemAllTaxSum(this.currentOrder.items[newIndex], true), 2));
        }

        // Apply all promotions across the whole cart (AOP → BXY → AOO)
        if (!isRefundContext) {
          this.reEvaluatePromotions();
        }

        this.currentOrder.balance_remaining = Math.max(0, this.currentOrderSum);
        const itemId = (this.currentOrder.items[newIndex]).id;

        // UI ACTIONS (Popups) - These should appear instantly now
        if(!product.refundModifiers) {
          if (product["modifier_groups"].length > 0) this.modifiersOpen(itemId, true);
        }
        if (product["price_adjust"]) {
          this.showPriceAdjustModal(this.currentOrder.items[(newIndex)]);
        }
        if (product["qty_adjust"]) this.changeQuantity(itemId);

        if (!product["modifier_groups"].length) {
          this.calculateItemCashDiscountChange(this.currentOrder.items.length - 1, true);
        }

        if (product.refundModifiers && product.modifiers && product.modifiers.length > 0) {
          await this.savePredefinedModifiers(product.modifiers, this.products.find(foundProduct => foundProduct.id === product.id)?.modifier_groups, this.currentOrder.items.length - 1);
        }

        await this.countSubtotal();

        // Save locally to Vuex/IndexedDB immediately for responsiveness
        if (this.currentOrder) {
          this.currentOrder.updated_at = new Date().toISOString();
          this.$store.dispatch('orders/saveOrder', this.currentOrder);
        }
      } catch (error) {
        console.error('Error adding item to order:', error);
      } finally {
        this.bufferHelper.itemsSavingCounter--;
        // Lock release is handled in processItemQueue
      }
    },
    markImportedOnlinePromotionDiscounts() {
      if (!this.currentOrder || Number(this.currentOrder.type) !== 1 || !Array.isArray(this.currentOrder.items)) return false;

      let changed = false;
      this.currentOrder.items.forEach((item, index) => {
        if (!item || item.void_status) return;
        const hasDiscount = (Number(item.discount) || 0) > 0 || (Number(item.wholeDiscount) || 0) > 0;
        if (!hasDiscount || item.isPromotionItem || item.promotion_applied) return;

        this.currentOrder.items[index].promotion_applied = true;
        this.currentOrder.items[index].isPromotionItem = Number(item.price) === 0;
        this.currentOrder.items[index].applied_promotions = Array.isArray(item.applied_promotions)
          ? item.applied_promotions
          : [];
        changed = true;
      });
      return changed;
    },
    async reEvaluateImportedOnlineOrderPromotions() {
      if (!this.currentOrder || Number(this.currentOrder.type) !== 1) return false;
      if (this.$store.getters['orders/refundOrderId']) return false;
      if (!Array.isArray(this.currentOrder.items)) return false;

      const hasImportedPromotionDiscount = this.currentOrder.items.some((item) =>
        item && !item.void_status && (
          (Number(item.discount) || 0) > 0 ||
          (Number(item.wholeDiscount) || 0) > 0 ||
          item.isPromotionItem ||
          item.promotion_applied
        )
      );
      if (!hasImportedPromotionDiscount) return false;

      const promotions = this.$store.getters['promotions/allPromotions'] || [];
      if (promotions.length === 0) return false;

      this.markImportedOnlinePromotionDiscounts();
      await this.reEvaluatePromotions({ channel: 'online_order' });
      return true;
    },
    reEvaluatePromotions(options = {}) {
      if (this._isReEvaluatingPromotions) return;
      this._isReEvaluatingPromotions = true;
      try {
        const isRefundContext = !!(this.$store.getters['orders/refundOrderId']);
        if (isRefundContext) return;
        if (!this.currentOrder || !Array.isArray(this.currentOrder.items)) return;

        // If cashier applied a manual order-level discount, don't override it with promotions
        if (this.currentOrder.discount_orders > 0 && !this.currentOrder.promotion_applied) return;

        const channel = options.channel || (Number(this.currentOrder.type) === 1 ? 'online_order' : 'point_of_sale');
        const promotions = channel === 'online_order'
          ? (this.$store.getters['promotions/allPromotions'] || [])
          : this.$store.getters['promotions/activePromotions'];

        // Clear previous promotion discounts from items (preserve manual item discounts)
        this.currentOrder.items.forEach((item, i) => {
          if (!item || item.void_status) return;
          if (item.isPromotionItem || item.promotion_applied) {
            this.currentOrder.items[i].discount = 0;
            this.currentOrder.items[i].wholeDiscount = 0;
            this.currentOrder.items[i].discountPropotion = 0;
            this.currentOrder.items[i].isPromotionItem = false;
            this.currentOrder.items[i].promotion_applied = false;
            this.currentOrder.items[i].applied_promotions = [];
          }
        });

        if (!promotions || promotions.length === 0) {
          if (this.currentOrder.promotion_applied) {
            this.currentOrder.discount_orders = 0;
            this.currentOrder.discount_orders_raw_value = null;
            this.currentOrder.discount_orders_raw_type = null;
            this.currentOrder.promotion_applied = false;
            this.currentOrder.applied_order_promotions = [];
          }
          this.countSubtotal();
          return;
        }

        // Enrich active items for the evaluator: set id = product ID, add categories
        const activeItems = [];
        const activeIndexMap = []; // enrichedIdx → currentOrder.items index
        this.currentOrder.items.forEach((item, i) => {
          if (!item || item.void_status) return;
          const product = (this.products || []).find((p) => p.id === item.product);
          activeItems.push({
            ...item,
            id: item.product,
            categories: product ? (product.categories || []) : [],
            // Trust the live catalog record over a possibly-stale cart-item
            // copy — a manager may have flipped this mid-shift.
            allow_discounts: product ? product.allow_discounts !== false : item.allow_discounts !== false
          });
          activeIndexMap.push(i);
        });

        if (activeItems.length === 0) {
          this.countSubtotal();
          return;
        }

        const result = applyPromotionsToCart(activeItems, promotions, { channel, employee: this.discountEmployee });

        // Write results back.
        // applyPromotionsToCart returns total-line discount values; POS getItemSubtotal
        // applies discount per-unit ((price - discount) * qty), so divide by qty.
        result.items.forEach((evaluated, enrichedIdx) => {
          const i = activeIndexMap[enrichedIdx];
          const qty = Math.max(this.currentOrder.items[i].quantity || 1, 1);

          // Preserve manual (non-promotion) item discount
          const hasManualDiscount = (this.currentOrder.items[i].discount || 0) > 0
            && !this.currentOrder.items[i].isPromotionItem
            && !this.currentOrder.items[i].promotion_applied;

          if (!hasManualDiscount) {
            // Store exact per-unit fraction — do NOT round here.
            // getItemSubtotal uses Decimal and rounds only at the final toDecimalPlaces(2),
            // so (price − discount_per_unit) × qty stays precise and matches biyo-order's
            // (price × qty − discount_total) arithmetic.
            const perUnitDiscount = evaluated.discount > 0
              ? evaluated.discount / qty
              : 0;
            this.currentOrder.items[i].discount = perUnitDiscount;
            // Convert string labels to the {promotionName} shape the UI expects
            this.currentOrder.items[i].applied_promotions = (evaluated.promotionLabels || [])
              .map((name) => ({ promotionName: name }));
          }

          const perUnitWhole = evaluated.wholeDiscount > 0
            ? evaluated.wholeDiscount / qty
            : 0;
          this.currentOrder.items[i].wholeDiscount = perUnitWhole;
          this.currentOrder.items[i].discountPropotion = perUnitWhole;

          if (!hasManualDiscount) {
            const flags = promotionItemFlags(
              this.currentOrder.items[i],
              this.currentOrder.items[i].discount || 0,
              perUnitWhole
            );
            this.currentOrder.items[i].promotion_applied = flags.promotion_applied;
            this.currentOrder.items[i].isPromotionItem = flags.isPromotionItem;
          }
        });

        // AOO total for the order-level discount fields
        const aooTotal = setTaxRound(
          result.items.reduce((s, item) => s + (item.wholeDiscount || 0), 0)
        );

        this.currentOrder.discount_orders = aooTotal;
        this.currentOrder.discount_orders_raw_value = aooTotal > 0 ? aooTotal : null;
        this.currentOrder.discount_orders_raw_type = aooTotal > 0 ? 2 : null;
        this.currentOrder.promotion_applied = result.discountTotal > 0;
        // applied_order_promotions: only AOO entries (for the order-level UI row)
        this.currentOrder.applied_order_promotions = result.appliedPromotions
          .filter((p) => p.promotion_type === 'AOO')
          .map((p) => ({ promotionName: p.name }));

        this.countSubtotal();
      } catch (error) {
        console.error('Error re-evaluating promotions:', error);
      } finally {
        this._isReEvaluatingPromotions = false;
      }
    },
    selectCategory: function (catId) {
      this.currentCategory = catId;
      this.$store.dispatch('products86d/reconcileFromSnapshot').catch(() => {});
    },
    getProductListPaginated () {
      const products = this.filteredProductsByCategory;
      this.paginatedProducts = [...this.paginatedProducts, ...products.splice(0, this.pagination.perPage)];

      return this.paginatedProducts;
    },
    async holdOrder (payload) {
      if (this.$route.params.id === "new" || this.currentOrder.id === 0) return true;
      await this.releaseOrderPresence();

      // set the current order status to hold (mutate the live store-backed order
      // in place; the proxy makes currentOrder the single source of truth)
      this.currentOrder.status = ORDER_STATUS.HOLD;
      this.currentOrder.hold_date = new Date().toISOString();
      this.currentOrder.description = payload.description;

      if (typeof this.sendBlankPayloadToCustomerDisplay === 'function') {
        this.sendBlankPayloadToCustomerDisplay();
      }

      await this.getOrderSync().flush({ queue: true, trigger: "hold_order" });

      if(this.showSelectedOrder){
        this.$router.push({name:'OrdersList'})
        this.setActiveOrderState(false)
        return null;
      }

      // If in Quick Service mode, always redirect to new order regardless of table
      if (this.terminalMode === "QUICK_SERVICE") {
        await router.push({
          name: "NewOrderRedirect",
          params: {}
        }, () => {
        });
        return null;
      }

      // For Table Service mode, handle table navigation
      if (!this.currentOrder.table) {
        await router.push({
          name: "NewOrderRedirect",
          params: {}
        }, () => {
        });
        return null;
      }

      // Now check if there are any remaining open orders on this table
      this.table = this.$store.getters.getTableWithOrders(this.currentOrder.table);
      this.table.tableOrders.length === 0
        ? await router.push({ name: "Tables" })
        : await router.push({ name: "TableOrders", params: { id: this.currentOrder.table } }, () => {
        });
    },
    updateLocalId: async function (msgObj) {
      if (!this.currentOrder || !this.currentOrder.id) return true;

      if (this.currentOrder.id === msgObj.localID && this.currentOrder.id !== msgObj.remoteID) {
        this.currentOrder.id = msgObj.remoteID;
        if (this.$route.name === "Order") {
          await router.replace({ name: "Order", params: { id: msgObj.remoteID } }, () => {
          });
        }
        await this.sync(true, false);
      }
    },
    updateCustomerId: async function (msgObj) {
      if (!this.currentOrder || !this.currentOrder.customer) return true;
      if (this.currentOrder.customer.id === msgObj.localID && this.currentOrder.customer.id !== msgObj.remoteID) {
        this.currentOrder.customer.id = msgObj.remoteID;
      }
    },
    searchByBarcode: function (num) {
      this.search.value = num;
      if (num.length === 0) return false;
      let searchValue = {
        value:''
      }
        searchValue.value = this.search.value
        let products = this.searchProductByName(this.search.value); // 84000
        if(products.length === 0){
          searchValue = this.checkForSKU(this.search.value)
          products = this.searchProductByName(searchValue?.value);
        }

      if (products.length === 0) {
        this.search.value = "";
        this.search.visible = false;

        return this.$bvModal.show("modal-product-not-found");
      }
      else{
        const selectedProduct = {...products[0]}
        if(searchValue.price)
        {
          selectedProduct.price = searchValue.price
          selectedProduct.price_base = searchValue.price
        }
         return this.addItemToOrder(selectedProduct);
      }
    },
    getSearchProductDisplayName(product) {
      // Shared, pure resolver (Step 2); honors the kitchen_name_pos setting.
      return resolveSearchProductDisplayName(product, this.terminal);
    },
    showProductInfo(product) {
      // Drive the single shared product-info modal off the selected product
      // instead of one <b-modal> per tile.
      this.selectedInfoProduct = product;
      this.$bvModal.show("modal-show-info");
    },
    getProductDisplayName(product) {
      // Shared, pure resolver (Step 2); honors the kitchen_name_pos setting.
      return resolveProductDisplayName(product, this.terminal);
    },
    barcodeInputChange: function (num) {
      this.search.value = num;
    },
    hideBarcodeNumpad: function () {
      if (this.$refs.orderSearchBar) this.$refs.orderSearchBar.hideBarcodeNumpad();
    },
    typeDiscount: function (num) {
      if (num === "C") {
        this.discount.raw = "0";
        this.discount.val = "0";
      } else {
        if (this.discount.changed) {
          if (this.discount.raw.length < 8) {
            this.discount.raw = this.discount.raw.concat(num + "");
          }
          this.discount.val = (parseFloat(this.discount.raw) / 100).toString();
        } else {
          this.discount.raw = num + "";
          this.discount.val = (parseFloat(this.discount.raw) / 100).toString();
        }
        this.discount.changed = true;
      }
    },
    changeDiscount: async function (itemId) {
      const index = this.currentOrder.items.findIndex(currentItem => currentItem.id === itemId);
      this.discount.changed = false;
      // convert values back to percentage if the discount was given in percentage
      if (this.discountDetails.type === 1) {
        // calculate modSum first
        const mods = this.currentOrder.items[index].modifiers.filter(mod => mod.void_status === false);
        let modSum = 0;
        for (const mod of mods) modSum += mod.price_original || mod.price || 0;
        const itemPrice = this.currentOrder.items[index].price || 0;
        const itemTotal = itemPrice + modSum;
        //reverting values back to percentage to show in the discount modal
        this.discount.val = itemTotal > 0 ? parseFloat((parseFloat(this.currentOrder.items[index].discount) * 100) / parseFloat(itemTotal.toFixed(2))) + "" : "0";
        this.discount.raw = itemTotal > 0 ? parseFloat((parseFloat(this.currentOrder.items[index].discount) * 100) / parseFloat(itemTotal.toFixed(2)))  + "" : "0";
        this.discount.type = this.discountDetails.type;
        this.discount.index = index;
      } else {
        this.discount.val = this.currentOrder.items[index].discount + "";
        this.discount.raw = this.currentOrder.items[index].discount + "";
        this.discount.type = 2;
        this.discount.index = index;
      }
      window.addEventListener("keydown", this.onKeyItemDiscount);
      // render modal
      this.$bvModal.show("modal-discount");
    },
    changeDiscountType: async function () {
      this.discount.type = this.discount.type === 1 ? 2 : 1;
    },
    setPredefinedDiscount: async function (index) {
      this.discount.type = this.allDiscounts[index].type;
      this.discount.val = this.allDiscounts[index].value;
    },
    openPointsRedemptionModal() {
      if (!this.currentOrder.customer) return;

      const customerPoints = this.currentOrder.customer.rewards_points || this.currentOrder.customer.reward_points || this.currentOrder.customer.points_balance || 0;

      // Find existing points redemption item
      const existingItem = this.currentOrder.items.find(item => item.name === 'Points Redeemed' && !item.void_status);
      this.currentPointsRedemption = existingItem ? Math.abs(existingItem.quantity) : 0;

      // Calculate max allowed based on the order total the customer actually
      // pays (merch + tax, net of any order-level discount), via the single
      // pricing engine (src/pricing) so this matches the auto-adjust logic in
      // orderMixin.js `countSubtotal` instead of duplicating (and drifting
      // from) it. The engine already excludes the existing "Points Redeemed"
      // line from its totals, so there's no need to "add it back" here.
      const pointsContext = computePointsContext(this.currentOrder, {
        rewardsConversionRate: this.rewardsConversionRate,
        isTaxInclusive: this.isTaxInclusive,
      });

      // Cap at customer's available points
      this.maxRedeemablePoints = Math.min(customerPoints, pointsContext.maxPointsByValue);

      this.$bvModal.show('modal-points-redemption');
    },
    async savePointsRedemption(points) {
      this.$bvModal.hide('modal-points-redemption');

      // Ensure we have an order context so the points item can persist
      if (this.$route.params.id === "new" && !this.currentOrder.id) {
        this.createNewOrder();
        router.replace({ name: "Order", params: { id: this.currentOrder.local_id } });
      }

      // Remove existing points item if any
      const existingIndex = this.currentOrder.items.findIndex(item => item.name === 'Points Redeemed' && !item.void_status);

      const pointsProduct = (this.products || []).find(p => p && p.name === 'Points Redeemed');
      const pointsProductId = pointsProduct ? pointsProduct.id : null;

      if (points <= 0) {
        if (existingIndex !== -1) {
          await this.removeItemFromOrderProcessing(this.currentOrder.items[existingIndex].id);
          this.countSubtotal();
          if (!this._batchAddingItems) {
            await this.sync(false, true);
          }
        }
        return;
      }

      // Update or Add
      // Price is positive per point (value)
      // Quantity is negative (deducting points)
      const price = this.rewardsConversionRate;
      const quantity = -1 * points;

      const createPointsItemFromProduct = (product, localItemId, itemLocalId) => {
        const baseItemPrice = parseFloat(toFixed(price, 2));
        const itemSubtotal = quantity * baseItemPrice;
        return {
          product: product.id,
          name: product.name,
          quantity: quantity,
          price: baseItemPrice,
          price_base: baseItemPrice,
          price_before_tax: baseItemPrice,
          subtotal: itemSubtotal,
          grand_total: itemSubtotal,
          cost: (product.cost || 0),
          id: localItemId,
          local_id: itemLocalId,
          discount: 0,
          modifiers: [],
          notes: "",
          void_status: 0,
          employee: this.employee ? this.employee.id : null,
          printers: product.printers,
          has_kitchen_updates: !!(product.printers && product.printers.length),
          sent_to_kitchen: false,
          tax_rate_value: 0,
          tax_status: 1,
          special_prices_items: product.special_prices_items,
          default_price: product.price,
          terminal_id: this.terminal.id,
          tax: 0,
          tax_items: [],
          created_at: new Date().toISOString(),
          wholeDiscount: 0,
          discountPropotion: 0
        };
      };

      if (existingIndex !== -1) {
          // Update existing using the same shape as product-grid items
          const existingItem = this.currentOrder.items[existingIndex];
          const localItemId = existingItem.id || this.getItemIterator();
          const itemLocalId = existingItem.local_id || localItemId;
          const updatedItem = createPointsItemFromProduct(pointsProduct, localItemId, itemLocalId);
          if (existingItem.sent_to_kitchen) {
            updatedItem.sent_to_kitchen = true;
          }
          this.$set(this.currentOrder.items, existingIndex, updatedItem);
          this.countSubtotal();
      } else {
        if (!pointsProductId) {
          this.$store.dispatch("errorModal/displayModalError", {
            modalId: "order-error",
            errorBody: {
              title: "Rewards setup missing",
              info: "Product 'Points Redeemed' was not found in the products list."
            }
          });
          return;
        }
        // Add new
        const localItemId = this.getItemIterator();
        const newItem = createPointsItemFromProduct(pointsProduct, localItemId, localItemId);

        // Add to items
        this.currentOrder.items.push(newItem);
        this.countSubtotal();
      }

      if (!this._batchAddingItems) {
        await this.sync(false, true);
      }
    },
    openOrderDiscount: function () {
      /**
       * This function opens the order discount modal directly from the bottom button
       * when retail_mode is enabled
       */
      this.$bvModal.show('modal-order-discount');
    },
    saveDiscountToItem: function () {
      if (!this.employee) return false;
      if (this.employee.role && this.employee.role === EMPLOYEE_ROLE.Cashier && !this.isVerified) {
        this.lockedAction = "item-discount";
        return this.$bvModal.show("enter-pin-modal");
      }
      const index = this.discount.index;

      if (this.discount.val !== "") {
        let newDiscount = 0;
        const item = this.currentOrder.items[index];

        const mods = item.modifiers ? item.modifiers.filter(mod => mod.void_status === false) : [];
        let modSum = 0;
        for (const mod of mods) modSum += (mod.price_original || mod.price || 0);
        const itemPrice = item.price || 0;
        const itemTotal = itemPrice + modSum;

        const discountType = parseInt(this.discount.type);

        if (discountType === 2) {
          // Currency Discount ($)
          newDiscount = parseFloat(parseFloat(this.discount.val).toFixed(2));
          if (newDiscount > itemTotal) {
            newDiscount = itemTotal;
          }
          this.setOrderDiscount(this.discount);
        } else {
          // Percentage Discount (%)
          newDiscount = parseFloat((itemTotal * (parseFloat(this.discount.val) / 100)).toFixed(2));
          this.setOrderDiscount(this.discount);
        }

        window.removeEventListener("keydown", this.onKeyItemDiscount);

        // 1. Update Discount Value
        this.currentOrder.items[index].discount = newDiscount;

        // 2. MARK AS MANUAL DISCOUNT (PROTECTION FROM WATCHER)
        // This ensures reEvaluatePromotions won't wipe this discount
        this.currentOrder.items[index].isPromotionItem = false;
        this.currentOrder.items[index].promotionId = null;
        this.currentOrder.items[index].promotionName = null;

        // 3. Recalculate Taxes
        this.currentOrder.items[index].tax_items = this.calculateItemTaxItems(this.currentOrder.items[index]);
        this.currentOrder.items[index].tax = this.currentOrder.items[index].tax_status === 0
            ? parseFloat(toFixed((calculateItemTaxTotal(this.currentOrder.items[index])), 2))
            : 0;

        // 4. Recalculate Totals
        this.currentOrder.items[index].subtotal = getItemSubtotal(this.currentOrder.items[index]);
        this.currentOrder.items[index].grand_total = parseFloat(getItemGrandTotal(this.currentOrder.items[index], this.isTaxInclusive).toFixed(2));

        this.$bvModal.hide("modal-discount");
        if (this.employee.role === EMPLOYEE_ROLE.Cashier) this.isVerified = false;
      }

      this.countSubtotal();
      this.scheduleOrderSync();

      if (this.currentOrder.discount_orders_raw_value) {
        this.countSubtotal();
      }

      this.discount.val = "";
    },
    searchProductByName: function (textToSearch) {
      const searchTerm = textToSearch.toLowerCase().trim();
      const isKitchenNamePosEnabled = isTerminalSettingEnabled(this.terminal, 'kitchen_name_pos');
      return this.products.filter(product=>{
        // Gift cards are never searchable — they can only be added via
        // "More Options" > Gift Card. Guard here covers name, kitchen-name
        // and barcode branches below in one place. Keyed off the store's
        // configured gift-card id, not a hardcoded -2.
        if (Number(product.id) === this.giftCardProductId) {
          return false;
        }
        // Check if kitchen_name_pos is enabled for kitchen name search
        if (this.isKioskMode && this.isProduct86d(product.id)) {
          return false;
        }

        // Search by name - partial match allowed
        if (product.name.toLowerCase().includes(searchTerm) && product.id !== -2 && product.id !== -3 && product.name !== 'Points Redeemed') {
          return product
        }
        // Search by kitchen_display_name if enabled
        else if (isKitchenNamePosEnabled && product.kitchen_display_name &&
                 product.kitchen_display_name.toLowerCase().includes(searchTerm) && product.id !== -2) {
          return product;
        }
        // Search by barcode - EXACT match only
        else if(product.barcode){
          if(product.barcode.includes(",")){
            // Multiple barcodes - check each one for exact match
            const exists = product.barcode.split(",").find(barCode => barCode.toLowerCase().trim() === searchTerm)
            if(exists)
              return product
          }
          else if(product.barcode.toLowerCase().trim() === searchTerm){
            return product
          }
        }
      });
    },
    saveDiscountToOrder: async function ({ value, discountType }) {
      /**
       * This function is used to calculate the discount on the whole order
      */

      // Validate discount based on type
      if (discountType === 1) {
        // For percentage discount, validate the percentage value directly (max 100%)
        if (value > 100) {
          this.$store.dispatch("errorModal/displayModalError", {
            modalId: "order-error",
            errorBody: {
              title: "Value error",
              info: `Discount amount is more than the value requested`
            }
          });
          return;
        }
      } else if (discountType === 2) {
        // For fixed amount discount, validate against order total
        let orderTotal = getOrderGrantTotal(this.currentOrder, this.products, this.isTaxInclusive);
        let discountAmount = parseFloat(parseFloat(value).toFixed(2));

        if (discountAmount > orderTotal) {
          this.$store.dispatch("errorModal/displayModalError", {
            modalId: "order-error",
            errorBody: {
              title: "Value error",
              info: `Discount amount is more than the value requested`
            }
          });
          return;
        }
      }

      this.currentOrder.discount_orders_raw_value = value;
      this.currentOrder.discount_orders_raw_type = discountType;
      this.currentOrder.discount_orders = value;

      this.currentOrder.promotion_applied = false;
      this.currentOrder.applied_order_promotions = [];

      if (value === 100 && discountType === 1)
        this.currentOrder.whole_order_discount = true
      else
        this.currentOrder.whole_order_discount = false
      await this.countSubtotal();
      // await this.sync();
    },
    displayPaymentDetails: function () {
      return this.isDisplayPaymentDetails = !this.isDisplayPaymentDetails;
    },
    goToCheckout: async function () {
      /**
       * This function syncs the order and redirect to checkout screen
      */
      if (!this.isPaymentTypesAvailable) return;
      this.isLoad = true;
      this.releaseLockedBy = false;
      this._skipDestroySync = true;
      this.isLoad = false;
      // Sync to backend before going to checkout
      await this.sync(true, true);
      if (this.$route.params.referenceId) {
        const referencedId = this.$route.params.referenceId
        await this.$router.push({
          name: "OrderCheckout",
          params: { id: (this.currentOrder.id || this.currentOrder.local_id), referenceId: referencedId }
        });
      }
      else {
        await this.$router.push({
          name: "OrderCheckout",
          params: { id: (this.currentOrder.id || this.currentOrder.local_id)}
        });
      }
    },
    verifyAction (employee) {
      if (!employee) {
        return this.isVerified = false;
      }
      this.isVerified = true;

      switch (this.lockedAction) {
        case "item-discount":
          this.saveDiscountToItem();
          break;
        case "remove-item":
          this.removeItemFromOrderProcessing(this.lockedItemId, this.lockedVoidReason);
          break;
        case "remove-order":
          this.cancelOrderProcessing({ voidReason: this.lockedVoidReason });
          break;
        case "item-qty":
          this.saveNewQuantity();
          break;
      }
      this.lockedVoidReason = null;
      this.isVerified = false;

      return this.$bvModal.hide("enter-pin-modal");
    },
    checkForCompletedOrder(orderID=null){
      let orderKey = null
      if(orderID){
        orderKey = orderID.toString().includes("_") ? orderID
        : parseInt(orderID);

       const orderInit =  {...this.$store.getters["orders/getOrderByIdOrLocalId"](orderKey)}
       if(orderInit.status === ORDER_STATUS.COMPLETED){
        this.$bvModal.show('complete-order-error')
        return true
       }
      }
      return false
    },
    async errorModalCallBack(){
      this.resetCurrentOrder()
      if (this.terminalMode === "QUICK_SERVICE") {
        // await this.emptyOrder()
        return await this.$router.push({
					name: "Order",
					params: {
						tableID: null,
						id: "new"
					}
				});
			} else if (!this.currentOrder.table) {
				return await router.push({ name: "Tables" });
			}
			const table = this.$store.getters.getTableWithOrders(this.currentOrder.table);
			if (table && table.tableOrders && table.tableOrders.length === 0) {
				await router.push({ name: "Tables" });
			} else if (this.table && this.table.tableOrders) {
				await router.push({ name: "TableOrders", params: { id: this.currentOrder.table } });
			} else if (this.isKioskMode) {
				await router.push({ name: "KioskHomeV2" });
			}
    },
    resetCurrentOrder() {
      this.currentOrder = {
        customer: null,
        localID: 0,
        subtotal: 0,
        items: [],
        table: null,
        tax_total: 0,
        discount_total: 0,
        order_discount: 0,
        status: 1,
        open_date: null,
        number: "0",
        discount_orders: 0,
        payments: [],
        receipt_kitchen_printed: false,
        description: "",
        dining_option_type: null,
        cash_discount: false,
        discount_orders_raw_value: null,
        discount_orders_raw_type: null,
        isActive:false //isActive variable to determine if current order is open the pos window
      }
    },

    async deleteItemFromModifiers() {
      const index = this.modifiers.currentItemIndex;
      const item = this.currentOrder.items[index];
      if (item) {
        // Pass true to skip confirmation
        const result = await this.removeItemFromOrder(item.id, true);
        if (result) {
          this.modifiersShow = false;
        }
      }
    },

    async saveGiftCard(event) {
      if (!this.activeGiftCard || !this.activeGiftCard.gift_card_number) {
        this.$bvToast.toast("Gift card number is missing. Please scan or enter a valid gift card before adding.", {
          title: "Gift Card Error",
          variant: "danger",
          solid: true,
        });
        return;
      }

      if ((this.$route.params.id === "new" && !this.currentOrder?.id) || !this.currentOrder) {
        const newOrder = await this.$store.dispatch('orders/initNewOrder',{
          related_terminal: this.terminal.id,
          table: this.$route.params.tableID || undefined,
        });
        this.currentOrder = newOrder;

        applyDiningOption(this.currentOrder, {
          diningOptions: this.terminal["dining_options"],
          defaultDiningOption: this.default_dining_option,
          isTableService: this.terminalMode === "TABLE_SERVICE",
        });

        this.$store.dispatch("orders/saveOrder", this.currentOrder);

        if (this.$route.params.id === "new") {
          router.replace({ name: "Order", params: { id: this.currentOrder.id || this.currentOrder.local_id, tableID: this.$route.params.tableID } });
        }
      }

      const giftCardProduct = this.products.find(product => product.id === -2);
      if (!giftCardProduct) {
        console.error("Gift Card product (-2) not found");
        return;
      }

      const price = event.gift_card_new_credit;
      const newItem = this.buildOrderItem(giftCardProduct, {
        quantity: 1,
        price: price
      });

      newItem.gift_card_number = this.activeGiftCard.gift_card_number;
      newItem.name = `Gift Card (Card Number: ${this.activeGiftCard.gift_card_number})`;
      newItem.notes = `Gift Card: ${this.activeGiftCard.gift_card_number}`;

      this.currentOrder.items.push(newItem);

      this.countSubtotal();

      this.$store.dispatch('orders/saveOrder', this.currentOrder);

      // Do not force immediate queue sync here.
      // Gift-card flow already goes through debounced order sync below; forcing both paths
      // can produce duplicate queue submissions for the same item.

      let selectedGiftCards = [...this.selectedGiftCards];
      selectedGiftCards.push({
        gift_card_number: this.activeGiftCard.id,
        amount_paid: price,
        transaction_balance: parseFloat(
            (this.activeGiftCard.credit + price).toFixed(2),
        ),
        transaction_type: GIFT_CARD_TRANSACTION_TYPE.CREDIT,
        terminal: this.terminal.id,
        order: this.currentOrder.id,
        is_applied: true,
        local_id: this.currentOrder.local_id,
        order_item_local_id: newItem.local_id,
        card_number: this.activeGiftCard.gift_card_number,
      });

      this.setSelectedGiftCards(selectedGiftCards);

      this.scheduleOrderSync();
    },
  }
};
</script>

<style lang="scss">

/* Product availability modal — 86 switch uses danger red when on */
.switch-product-86.custom-switch .custom-control-input:checked ~ .custom-control-label::before {
  background-color: #dc3545;
  border-color: #dc3545;
}

.switch-modifier-86.custom-switch .custom-control-input:checked ~ .custom-control-label::before {
  background-color: #dc3545;
  border-color: #dc3545;
}

.product-86-modifiers-scroll {
  max-height: 48vh;
  overflow-y: auto;
}

.modifiers-item.modifiers-item--86d {
  opacity: 0.38;
  cursor: not-allowed;
}

.modifier-delete-btn{
  left: 10px;
  right: auto;
}

.products .product-price {
  position: absolute;
  bottom: 5px;
  right: 5px;
  font-size: .80rem;
  background: #0000005c;
  border-radius: 10px;
  padding-left: 10px;
  padding-right: 10px;
}

.list-complete-item {
  transition: none;
}

.list-complete-enter, .list-complete-leave-to {
  opacity: 0;
  /*transform: translateY(30px);*/
}

.list-complete-leave-active {
  position: absolute;
}

.popup-price {

  .modal-dialog {
    max-width: 440px;
  }

  .modal-body {
    padding: 30px 42px;

    @media (max-width: 767.98px) {
      padding: 25px 12px;
    }
  }

  .modal-header {
    background: #FAFAFA;
    align-items: center;
    position: relative;
    justify-content: center;
    height: 72px;

    .btn {
      min-width: 84px;
      position: absolute;
      left: 16px;
      top: 50%;
      margin-top: -20px;

      @media (max-width: 767.98px) {
        left: .25rem;
      }
    }

    h5 {
      margin: 0;
      text-align: center;
    }
  }

  .modal-footer {

    border: none;
    position: absolute;
    width: 130px;
    text-align: center;
    top: 0;
    bottom: 0;
    background: #EBEBEB;
    border-radius: 0 10px 10px 0;
    right: -130px;
    border-left: 3px solid #2A7BFF;
    padding: 0;
    align-items: flex-start;
    overflow: hidden;
    height: 100%;

    .w-100 {
      height: 100%;
    }

    h5 {
      font-size: 16px;
      line-height: 19px;
      color: rgba(74, 74, 74, 0.717108);
      background: #FAFAFA;
      height: 71px;
      line-height: 71px;
      margin: 0;
      width: 100%;
    }

    ul {
      overflow-y: auto;
      height: calc(100% - 71px);

      li {
        padding: 1px 0;
      }

      a {
        display: block;
        height: 72px;
        width: 100%;
        font-size: 15px;
        line-height: 21px;
        color: #1E1E1E;
        text-decoration: none;
        background: #fff;
        transition: all .2s ease-in-out;
        padding: 10px;

        &.active,
        &:hover {
          background: #2889FA;
          color: #fff;
        }

        span {
          display: block;
          position: relative;
          top: 50%;
          transform: translateY(-50%);
        }
      }
    }
  }

  &-sidebar {
    .modal-content {
      border-radius: 10px 0px 0px 10px;
      position: initial;
    }

    .modal-header {
      border-radius: 10px 0px 0px 0rem;
    }

    .modal-body {
      border-radius: 0rem 0px 0px 10px;
    }
  }
}


.modifiers-sidebar-backdrop {
  background: #212121;
  width: 100%;
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  cursor: pointer;
  opacity: .5;
  z-index: 10;

  /*transition: all 0s;*/
}

.modifiers-sidebar-backdrop[aria-pressed="false"] {
  opacity: 0;
  z-index: -1;
}


.slide-enter-active,
.slide-leave-active {
  left: 0;
  transition: transform 0s;
}

.slide-enter,
.slide-leave-to {
  left: 100%;
  transition: all 0s;
}


.modifiers-sidebar-panel {
  position: absolute;
  z-index: 100;
  top: 0;
  background: #eeeeee;
  left: 0;
  width: 100%;
  bottom: 0;
}

.btn-disabled {
  cursor: not-allowed;
  pointer-events: none;
  opacity: 0.4;
}

.dropdown-menu {
  margin-top: 45px;
}


@media (max-width: 767.98px) {
  .list-complete-item {
    transition: none !important;
  }
}

.bounce-enter-active {
  animation: bounce-in .5s;
}

.bounce-leave-active {
  animation: bounce-in .5s reverse;
}

.navbar-search-res {
  overflow: auto;
  max-height: 70vh;
  position: absolute;

  li {
    height: auto;

    &:nth-child(even) {
      background: #E3F2FD;
    }
  }
}

.search-scroll-indicator {
  position: sticky;
  padding: 10px 22px;
  text-align: center;
  color: #1565C0;
  font-size: 13px;
  font-weight: 500;
  pointer-events: none;
  z-index: 1;
}

.search-scroll-up {
  top: 0;
  background: linear-gradient(rgba(255, 255, 255, 0.95), transparent);
}

.search-scroll-down {
  bottom: 0;
  background: linear-gradient(transparent, rgba(255, 255, 255, 0.95));
}

.search-color-tag {
  display: inline-block;
  width: 40px;
  height: 40px;
  border-radius: 4px;
  margin-right: 10px;
  flex-shrink: 0;
}

.search-price-pill {
  display: inline-block;
  background: #E3F2FD;
  color: #1565C0;
  padding: 6px 12px;
  border-radius: 16px;
  font-size: 14px;
  font-weight: 700;
  white-space: nowrap;
}

@keyframes bounce-in {
  0% {
    transform: scale(0);
  }
  50% {
    transform: scale(1.5);
    color: #fff;
    background: red;
  }
  100% {
    transform: scale(1);
  }
}

.sent_to_kitchen .align-items-center {
  font-style: italic;
  color: #888888;
}

.spiner-overbutton {
  position: absolute;
  top: 19px;
  z-index: 10;
  margin-left: auto;
  margin-right: auto;
  left: 0;
  right: 0;
  text-align: center;
}
.hidden {
  display:none !important;
}

/* Quantity Bar Integration Styles */
.qty-bar-column {
  flex: 0 0 auto !important;
  max-width: 80px !important;
  width: 80px !important;
  padding: 0 !important;
}

.qty-bar-mobile-col {
  padding: 0 !important;
}

@media (max-width: 767.98px) {
  .qty-bar-column {
    width: 100% !important;
    max-width: 100% !important;
  }
}
</style>
